import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";

import swal from "sweetalert2";

import { VacacionesService } from "../vacaciones.service";
import {
  Colaborador,
  DiaCalendario,
  notaVisible,
  Saldo,
  Vacacion,
} from "../../shared/model/vacaciones/vacacion.model";
import { generaFrc019 } from "../frc019";
import { agrupaPedidos, tramosDe } from "../pedidos-vacaciones";
import { FirmaFrcComponent } from "../firma-frc/firma-frc.component";
import { FrcFirmadoComponent } from "../frc-firmado/frc-firmado.component";
import { rhCargaPermiso, rhCargaVacacion, textoDocumento, vacacionConFrc } from "../frc-firmado/documento-rh";
import { idEnSesion } from "../frc-firmado/sesion";
import { filtraPersonas, OpcionPersona, opcionesDePersonas } from "../filtro-personas";
import {
  acumuladoUsado,
  acumuladoVista,
  gozadosEnCos,
  gozadosVista,
  paraElSiguiente,
  quedanVista,
  restantesVista,
} from "../saldo-vista";
import { PermisosCalendarioComponent } from "../../permisos/permisos-calendario/permisos-calendario.component";
import { CargaExcelVacacionesComponent } from "../carga-excel-vacaciones/carga-excel-vacaciones.component";
import { PermisosService } from "../../permisos/permisos.service";
import { Permiso } from "../../shared/model/permisos/permiso.model";
import {
  FilaAutorizacion,
  FiltroAutorizacion,
  filasDeAutorizacion,
  filtraAutorizaciones,
} from "./autorizaciones";

/** «Autorizaciones»: de cuántos renglones puede ser una página, lo que
 *  mide uno sencillo y lo que ocupan el encabezado de la tabla, su pie y
 *  el aire de abajo. */
const AUT_MIN = 3;
const AUT_MAX = 12;
const AUT_ALTO_RENGLON = 78;
const AUT_ALTO_FIJO = 138;

/** Los tres selectores de persona con buscador: el colaborador del panel
 *  de alta y, en el modal de días, de quién son y quién le autoriza. */
type CampoPersona = "colaborador" | "saldoQuien" | "saldoAutoriza";
const CAMPOS_PERSONA: readonly CampoPersona[] = ["colaborador", "saldoQuien", "saldoAutoriza"];

/**
 * Calendario de vacaciones del equipo.
 *
 * Pinta un mes a la vez. Cada periodo se guarda completo (del primer al
 * último día de descanso) y la cuadrícula decide qué días se colorean:
 * sábado y domingo nunca, porque no se trabaja. Así un periodo del
 * miércoles 2 al lunes 7 se ve como 2, 3, 4 y 7 — que es como lo vive
 * quien se va de vacaciones.
 */
@Component({
  selector: "app-calendario-vacaciones",
  standalone: true,
  templateUrl: "./calendario-vacaciones.component.html",
  styleUrls: ["./calendario-vacaciones.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    FirmaFrcComponent,
    FrcFirmadoComponent,
    PermisosCalendarioComponent,
    CargaExcelVacacionesComponent,
  ],
  // Sin providers de fecha a proposito: se usa el FechaEstandarAdapter
  // que la app registra en main.ts, para que aqui se lea dd/MM/yyyy
  // igual que en todas las demas pantallas.
})
export class CalendarioVacacionesComponent implements OnInit {
  /** Mes que se está viendo. Siempre el día 1, para no arrastrar horas. */
  mes = new Date();

  semanas: DiaCalendario[][] = [];
  vacaciones: Vacacion[] = [];
  colaboradores: Colaborador[] = [];

  cargando = false;

  /** Selector rapido de mes y anio, para no navegar de uno en uno. */
  saltoAbierto = false;
  mesElegido = 0;
  anioElegido = 0;
  /** El mes de la barra, que abre el salto: al cerrarlo, el foco vuelve aquí. */
  @ViewChild("botonMes") botonMes?: ElementRef<HTMLButtonElement>;
  /** El campo de días de la celda que se edita; recibe el foco al abrirla (antes lo pedía el atributo autofocus). */
  @ViewChild("campoDias") private campoDias?: ElementRef<HTMLInputElement>;

  /* ---- Lista de periodos ---- */
  /**
   * Que se esta viendo. El calendario, la lista de periodos vigentes o
   * el acumulado del anio: cada uno en lo suyo, para que la pantalla no
   * crezca hacia abajo.
   */
  pestana:
    | "calendario"
    | "autorizaciones"
    | "periodos"
    | "seguimiento"
    | "permisos"
    | "config" = "calendario";

  /** Solicitudes que este usuario puede resolver. Las manda el servidor
   *  ya filtradas: quien administra ve todas, un autorizador solo las
   *  de su gente. */
  pendientes: Vacacion[] = [];

  /** Filtro de la pestaña de configuración. */
  filtroConfig = "";

  /**
   * Días festivos en ISO: los del año anterior, el del mes y el siguiente,
   * para contar bien una vacación que cruza el año. Los manda el servidor.
   *
   * Es un Set para que la comprobación por día sea inmediata: se
   * consulta una vez por celda del calendario en cada ciclo.
   */
  festivos = new Set<string>();

  /** Área por la que se está filtrando. Vacío quiere decir todas. */
  areaFiltro = "";

  /** Paginado de configuración. Son 139 colaboradores: sin esto la
   *  pantalla se vuelve una tira interminable. */
  readonly TAMANOS = [10, 15, 100];
  porPaginaConfig = 10;
  paginaConfig = 1;

  resolviendo: number | null = null;
  pagina = 1;
  readonly PORPAGINA = 5;

  /** Los del anio completo, solo para la bitacora. */
  delAnio: Vacacion[] = [];
  /** De qué `delAnio` salió `agrupado`: se rearma solo cuando cambia. */
  private agrupadoDe: Vacacion[] | null = null;
  private agrupado: Vacacion[] = [];

  /**
   * Lo del año como solicitudes, para la tabla del Seguimiento: los tramos
   * de un pedido de días sueltos, juntos (15/10/2026). `delAnio` se queda
   * tramo por tramo para la vista del año, que cuenta días por mes.
   */
  get solicitudesDelAnio(): Vacacion[] {
    if (this.agrupadoDe !== this.delAnio) {
      this.agrupadoDe = this.delAnio;
      this.agrupado = agrupaPedidos(this.delAnio);
    }
    return this.agrupado;
  }
  /** De que anio es lo que hay en delAnio. null = no se ha cargado. */
  anioCargado: number | null = null;
  cargandoBitacora = false;

  /**
   * El periodo de vacaciones de cada quien vigente a `fechaSaldos`: uno por
   * persona. Desde el 24/09/2026 cada saldo es un periodo de aniversario de
   * su Alta IMSS (o el año calendario si no la tiene), así que en la misma
   * lista conviven periodos que empezaron en años distintos.
   */
  saldos: Saldo[] = [];

  /**
   * La fecha a la que el servidor eligió esos periodos (yyyy-MM-dd): hoy si
   * se está viendo el mes actual; si no, el punto medio del mes que se ve.
   * Se enseña arriba de la tabla de Configuración para que se sepa de qué
   * día son los números.
   */
  fechaSaldos = "";

  /** Paleta de la que se elige el color del periodo. */
  readonly COLORES = [
    "#0369a1", // azul
    "#0d9488", // verde azulado
    "#b45309", // ambar tostado
    "#be123c", // frambuesa
    "#15803d", // verde
    "#7c2d12", // terracota
    "#1e40af", // azul marino
    "#a16207", // mostaza
  ];

  /* ---- Panel de días asignados ---- */
  saldoAbierto = false;
  guardandoSaldo = false;
  borradorSaldo: Saldo = {};

  /* ---- Formulario ---- */
  panelAbierto = false;
  guardando = false;
  /** null en alta; el id del periodo cuando se esta corrigiendo. */
  editando: number | null = null;
  /** Copia del periodo antes de tocarlo: sus dias no deben contarse dos veces. */
  original: Vacacion | null = null;
  borrador: Vacacion = {};

  /**
   * La semana laboral. Sábado y domingo NO salen: no se trabaja, así que
   * ocupaban dos séptimas partes del ancho para no decir nada. Con cinco
   * columnas cada día gana casi la mitad de espacio y los nombres
   * completos por fin caben.
   */
  readonly diasSemana = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"];
  readonly nombresMes = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
  ];

  /**
   * Permisos esperando respuesta, para el aviso de la pestaña
   * «Autorizaciones» (hasta el 13/10/2026 lo llevaba la de Permisos).
   *
   * Se consulta aquí y no dentro de una pestaña porque su contenido no
   * existe hasta que alguien la abre: sin el número a la vista, un
   * permiso se queda esperando días sin que nadie se entere.
   */
  permisosPendientes = 0;

  /* ---- Autorizaciones ----
     13/10/2026 (Norman: «mejor otra pestaña que se llame Autorizaciones y
     ahí salgan las de vacaciones y permisos»). Lo que espera respuesta,
     en una tabla: las solicitudes de vacaciones (pendientes, que llegan
     con el calendario) y los permisos (cuentaPermisos). Los renglones los
     arma ./autorizaciones; aquí van el filtro y las páginas. */

  /** Los permisos que esperan respuesta, como los manda el servidor. */
  permisosPorResolver: Permiso[] = [];

  /** Vacaciones y permisos juntos, de lo más próximo a lo más lejano. */
  porAutorizar: FilaAutorizacion[] = [];

  /** Qué se ve de esa lista, y los renglones de la página que se pinta. */
  filtroAut: FiltroAutorizacion = "todas";
  autFiltradas = 0;
  autPagina: FilaAutorizacion[] = [];
  paginaAut = 1;
  totalPaginasAut = 1;
  /** Renglones por página: los que caben sin que la pantalla ruede (ver
   *  ajustaPaginaAut). */
  porPaginaAut = 6;
  /** La caja de la tabla, mientras se ve la pestaña. */
  @ViewChild("tablaAut") private tablaAut?: ElementRef<HTMLElement>;

  readonly FILTROS_AUT: { id: FiltroAutorizacion; texto: string; icono: string }[] = [
    { id: "todas", texto: "Todas", icono: "inbox" },
    { id: "vacaciones", texto: "Vacaciones", icono: "beach_access" },
    { id: "permisos", texto: "Permisos", icono: "assignment_turned_in" },
  ];

  constructor(
    private service: VacacionesService,
    private permisos: PermisosService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.mes = new Date(this.mes.getFullYear(), this.mes.getMonth(), 1);
    this.cargar();
    this.cuentaPermisos();
  }

  /**
   * El aviso de la pestaña. Es eso, un aviso: si falla se queda en cero
   * y no se le dice nada a nadie — que el calendario de vacaciones no se
   * vea por culpa del contador de permisos sería peor que no tenerlo.
   */
  cuentaPermisos(): void {
    // Solo los pendientes: consultar() traia ademas los propios, el
    // encabezado y los jefes (cuatro consultas, dos al organigrama)
    // para sacar un numero.
    this.permisos.porResolver().subscribe({
      next: (res) => {
        if (!res.codigoError) {
          this.permisosPorResolver = res.porResolver || [];
          this.permisosPendientes = this.permisosPorResolver.length;
          this.armaAutorizaciones();
          this.cdr.detectChanges();
          this.ajustaPaginaAut();
        }
      },
      error: () => {},
    });
  }

  /* ===================== Autorizaciones ===================== */

  /** Cuántas cosas esperan respuesta: la cuenta en rojo de la pestaña. */
  get totalPorAutorizar(): number {
    return this.pendientes.length + this.permisosPendientes;
  }

  get tipPorAutorizar(): string {
    const n = this.totalPorAutorizar;
    return n === 1 ? "Una solicitud espera tu respuesta" : `${n} solicitudes esperan tu respuesta`;
  }

  /** Vuelve a armar la tabla: cambió lo pendiente de vacaciones o de permisos. */
  private armaAutorizaciones(): void {
    this.porAutorizar = filasDeAutorizacion(
      this.pendientes,
      this.permisosPorResolver,
      (v) => this.diasHabiles(v),
      new Date().getFullYear(),
    );
    // Si el filtro se quedó sin ninguna (se resolvió la última), se quita.
    if (!this.cuantasAut(this.filtroAut)) {
      this.filtroAut = "todas";
    }
    this.pintaAutorizaciones();
  }

  /** La página que se ve, con el filtro puesto. */
  private pintaAutorizaciones(): void {
    const filas = filtraAutorizaciones(this.porAutorizar, this.filtroAut);
    this.autFiltradas = filas.length;
    this.totalPaginasAut = Math.max(1, Math.ceil(filas.length / this.porPaginaAut));
    this.paginaAut = Math.min(Math.max(this.paginaAut, 1), this.totalPaginasAut);
    const desde = (this.paginaAut - 1) * this.porPaginaAut;
    this.autPagina = filas.slice(desde, desde + this.porPaginaAut);
  }

  /**
   * De cuántas en cuántas: las que caben entre el encabezado de la tabla
   * y el pie de la ventana, para que la pantalla no ruede (lo mismo que en
   * «Tus vacaciones»). Se estima con el alto de un renglón sencillo y, ya
   * pintadas, si no caben (uno con nota o con suplencia mide más), una
   * menos hasta que quepan. Se mide al abrir la pestaña, al cambiar lo
   * pendiente y al cambiar el tamaño de la ventana.
   */
  private ajustaPaginaAut(): void {
    const tabla = this.tablaAut?.nativeElement;
    const panel = tabla?.closest(".main-panel") as HTMLElement | null | undefined;
    if (!tabla || !panel) {
      return;
    }
    const arriba = tabla.getBoundingClientRect().top - panel.getBoundingClientRect().top + panel.scrollTop;
    const libre = panel.clientHeight - arriba - AUT_ALTO_FIJO;
    this.ponPorPaginaAut(Math.max(AUT_MIN, Math.min(AUT_MAX, Math.floor(libre / AUT_ALTO_RENGLON))));
    while (this.porPaginaAut > AUT_MIN && panel.scrollHeight > panel.clientHeight + 1) {
      this.ponPorPaginaAut(this.porPaginaAut - 1);
    }
  }

  private ponPorPaginaAut(renglones: number): void {
    if (renglones !== this.porPaginaAut) {
      this.porPaginaAut = renglones;
      this.pintaAutorizaciones();
      this.cdr.detectChanges();
    }
  }

  @HostListener("window:resize")
  alCambiarVentana(): void {
    this.ajustaPaginaAut();
  }

  cuantasAut(filtro: FiltroAutorizacion): number {
    return filtraAutorizaciones(this.porAutorizar, filtro).length;
  }

  filtraAut(filtro: FiltroAutorizacion): void {
    this.filtroAut = filtro;
    this.paginaAut = 1;
    this.pintaAutorizaciones();
  }

  mueveAut(delta: number): void {
    this.paginaAut += delta;
    this.pintaAutorizaciones();
  }

  trackAut(_: number, f: FilaAutorizacion): string {
    return f.clave;
  }

  /* ===================== Carga ===================== */

  cargar(): void {
    this.cargando = true;
    // Se piden seis semanas completas: la cuadrícula muestra días del mes
    // anterior y del siguiente, y un periodo que arranca ahí también tiene
    // que pintarse.
    const { desde, hasta } = this.rangoVisible();
    const ref = this.fechaReferencia(desde, hasta);

    this.service.consultar(desde, hasta).subscribe({
      next: (res) => {
        this.cargando = false;
        if (res.codigoError) {
          this.aviso(res.mensajeError || "No se pudo cargar el calendario.", "error");
          this.cdr.detectChanges();
          return;
        }
        this.vacaciones = res.vacaciones || [];
        this.colaboradores = res.colaboradores || [];
        // Los selectores de persona salen de esta lista: se rearman con ella.
        this.armaPersonas();
        this.saldos = res.saldos || [];
        this.fechaSaldos = ref;
        this.pendientes = agrupaPedidos(res.pendientes || []);
        // Los colores dependen de los saldos, así que se arman DESPUÉS
        // de recibirlos, no antes.
        this.festivos = new Set<string>(res.festivos || []);
        // Ya con los festivos: los días de cada solicitud se cuentan con ellos.
        this.armaAutorizaciones();
        this.asignaColoresArea();
        this.pagina = 1;
        this.construirMes();
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargando = false;
        this.aviso("No se pudo cargar el calendario. Revisa tu conexión.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Pinta la lista de periodos que devuelve el servidor al guardar un
   * saldo. Si por algo no viniera, se pide el mes otra vez en vez de
   * dejar en la tabla números que ya no son. Los colores de área se
   * rearman porque un área puede aparecer o desaparecer.
   */
  private aplicaSaldos(saldos?: Saldo[]): void {
    if (!saldos) {
      this.cargar();
      return;
    }
    // Se guardó con el rango visible, así que vienen a la fecha de ese rango.
    const { desde, hasta } = this.rangoVisible();
    this.saldos = saldos;
    this.fechaSaldos = this.fechaReferencia(desde, hasta);
    this.asignaColoresArea();
  }

  /* ===================== Navegación ===================== */

  // Las tres cierran el salto si estaba abierto: sus selects se quedarían
  // en el mes de antes e «Ir» regresaría ahí.
  mesAnterior(): void {
    this.saltoAbierto = false;
    this.mes = new Date(this.mes.getFullYear(), this.mes.getMonth() - 1, 1);
    this.cargar();
  }

  mesSiguiente(): void {
    this.saltoAbierto = false;
    this.mes = new Date(this.mes.getFullYear(), this.mes.getMonth() + 1, 1);
    this.cargar();
  }

  irAHoy(): void {
    this.saltoAbierto = false;
    const hoy = new Date();
    this.mes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    this.cargar();
  }

  get tituloMes(): string {
    return `${this.nombresMes[this.mes.getMonth()]} ${this.mes.getFullYear()}`;
  }

  /* ===================== La cuadrícula ===================== */

  /** Lunes de la semana en la que cae el día 1. */
  private primerDiaCuadricula(): Date {
    const primero = new Date(this.mes.getFullYear(), this.mes.getMonth(), 1);
    // getDay(): 0 domingo … 6 sábado. La semana arranca en lunes.
    const desplazamiento = (primero.getDay() + 6) % 7;
    return new Date(
      primero.getFullYear(),
      primero.getMonth(),
      primero.getDate() - desplazamiento,
    );
  }

  private ultimoDiaCuadricula(): Date {
    const inicio = this.primerDiaCuadricula();
    // Seis semanas cubren cualquier mes sin importar en qué día empiece.
    return new Date(
      inicio.getFullYear(),
      inicio.getMonth(),
      inicio.getDate() + 41,
    );
  }

  /**
   * El rango que se le pide al servidor: las seis semanas de la cuadrícula.
   * Es el mismo al consultar y al guardar un saldo, para que el servidor
   * conteste con los periodos vigentes a la misma fecha que ya se ven.
   */
  private rangoVisible(): { desde: string; hasta: string } {
    return {
      desde: this.iso(this.primerDiaCuadricula()),
      hasta: this.iso(this.ultimoDiaCuadricula()),
    };
  }

  /**
   * A qué fecha elige el servidor el periodo de cada quien para ese rango
   * (VacacionService.fechaDeReferencia): HOY si cae en el MES que se ve;
   * si no, el punto medio de la cuadrícula, que siempre queda dentro de ese
   * mes. El mes y no la cuadrícula: la de octubre empieza el 28 de
   * septiembre. Aquí se repite sólo para DECIRLA arriba de la tabla: el
   * servidor no la recibe, la calcula igual por su cuenta.
   */
  private fechaReferencia(desde: string, hasta: string): string {
    const d = this.aFecha(desde);
    const h = this.aFecha(hasta);
    // Math.round y no floor: un cambio de horario en el navegador deja una
    // hora de más o de menos y no debe mover el día.
    const dias = Math.round((h.getTime() - d.getTime()) / 86400000);
    const medio = this.iso(new Date(d.getFullYear(), d.getMonth(), d.getDate() + Math.floor(dias / 2)));
    const hoy = this.iso(new Date());
    return hoy.substring(0, 7) === medio.substring(0, 7) ? hoy : medio;
  }

  private construirMes(): void {
    const semanas: DiaCalendario[][] = [];
    const cursor = this.primerDiaCuadricula();
    const hoyIso = this.iso(new Date());

    for (let s = 0; s < 6; s++) {
      const semana: DiaCalendario[] = [];
      // Solo de lunes a viernes. El cursor sigue avanzando de siete en
      // siete —la semana natural no cambia— pero del sábado y el
      // domingo no se crea celda.
      for (let d = 0; d < 5; d++) {
        const fecha = new Date(
          cursor.getFullYear(),
          cursor.getMonth(),
          cursor.getDate() + s * 7 + d,
        );
        const delMes = fecha.getMonth() === this.mes.getMonth();
        const iso = this.iso(fecha);

        semana.push({
          iso: delMes ? iso : null,
          numero: delMes ? fecha.getDate() : null,
          // Ya no hay celdas de fin de semana, pero el campo se conserva
          // porque el modelo lo comparte con la pantalla del colaborador.
          esFinDeSemana: false,
          esHoy: delMes && iso === hoyIso,
          periodos: delMes ? this.periodosDe(iso) : [],
          regresan: delMes ? this.regresanEl(iso) : [],
        });
      }
      semanas.push(semana);
    }

    // Se descartan las filas que quedaron enteras fuera del mes.
    //
    // Al quitar el fin de semana esto dejó de ser un caso raro: un mes
    // que termina en sábado tiene una última semana natural cuyos
    // únicos días son sábado y domingo, y sin ellos la fila queda
    // completamente vacía.
    this.semanas = semanas.filter((s) => s.some((c) => c.numero !== null));
  }

  private periodosDe(iso: string): Vacacion[] {
    return this.vacaciones.filter(
      (v) => !!v.fechaInicio && !!v.fechaFin && iso >= v.fechaInicio && iso <= v.fechaFin,
    );
  }

  private regresanEl(iso: string): Vacacion[] {
    return this.vacaciones.filter((v) => v.fechaRegreso === iso);
  }

  /* ===================== Resumen del mes ===================== */

  /** Los colaboradores que descansan algún día del mes que se ve. */
  get enDescanso(): Colaborador[] {
    const vistos = new Map<number, Colaborador>();
    for (const v of this.vacaciones) {
      if (v.idUsuario == null || vistos.has(v.idUsuario)) {
        continue;
      }
      vistos.set(v.idUsuario, {
        idUsuario: v.idUsuario,
        nombre: v.colaborador,
        color: v.color,
      });
    }
    return Array.from(vistos.values());
  }

  /* ===================== Salto de mes ===================== */

  abrirSalto(): void {
    this.mesElegido = this.mes.getMonth();
    this.anioElegido = this.mes.getFullYear();
    this.saltoAbierto = !this.saltoAbierto;
  }

  /** Tres anios atras y tres adelante: cubre la planeacion real. */
  get aniosDisponibles(): number[] {
    const hoy = new Date().getFullYear();
    const lista: number[] = [];
    for (let a = hoy - 2; a <= hoy + 3; a++) {
      lista.push(a);
    }
    return lista;
  }

  aplicarSalto(): void {
    this.mes = new Date(this.anioElegido, this.mesElegido, 1);
    this.cerrarSalto();
    this.cargar();
  }

  /**
   * Cierra el salto (Escape o «Ir») y regresa el foco al mes que lo abrió:
   * «Ir» desaparece con el salto y el foco se iba al <body>.
   */
  cerrarSalto(): void {
    this.saltoAbierto = false;
    this.botonMes?.nativeElement.focus();
  }

  /**
   * Cuántos días hábiles cubre un periodo. Ni fin de semana ni festivo:
   * tiene que dar lo mismo que el servidor y que la pantalla de
   * Solicitar, o el FRC-019 generado desde aquí imprimiría un día de
   * más cuando el periodo contiene un festivo.
   */
  diasHabiles(v: Vacacion): number {
    // Un pedido de días sueltos: los de sus tramos, no los del primero al último.
    return tramosDe(v).reduce((n, t) => n + this.habilesDelRango(t), 0);
  }

  private habilesDelRango(v: Vacacion): number {
    if (!v.fechaInicio || !v.fechaFin) {
      return 0;
    }
    let n = 0;
    const fin = this.aFecha(v.fechaFin);
    const cursor = this.aFecha(v.fechaInicio);
    while (cursor <= fin) {
      const d = cursor.getDay();
      if (d !== 0 && d !== 6 && !this.festivos.has(this.iso(cursor))) {
        n++;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return n;
  }

  /* ===================== Saldo de días ===================== */

  /**
   * El periodo del colaborador que está en pantalla: el vigente a
   * `fechaSaldos`. El servidor manda uno solo por persona.
   */
  saldoDe(idUsuario?: number): Saldo | null {
    if (idUsuario == null) {
      return null;
    }
    return this.saldos.find((s) => s.idUsuario === idUsuario) || null;
  }

  /**
   * ¿La fecha cae dentro del periodo? Es [inicio, fin): el fin es el
   * aniversario siguiente y ese día ya es del periodo que sigue, igual que
   * en el servidor (PeriodoSql). Las fechas son ISO, así que se comparan
   * como texto sin pasar por Date.
   */
  contiene(s: Saldo | null | undefined, fechaIso?: string | null): boolean {
    if (!s || !fechaIso) {
      return false;
    }
    // Sin fechas no se puede decir que no. No debería pasar: el servidor
    // las manda siempre llenas.
    if (!s.periodoInicio || !s.periodoFin) {
      return true;
    }
    const f = fechaIso.substring(0, 10);
    return f >= s.periodoInicio.substring(0, 10) && f < s.periodoFin.substring(0, 10);
  }

  /**
   * El periodo del que se descuentan las vacaciones que se capturan en el
   * panel: el que está en pantalla, SÓLO si el primer día cae en él (o si
   * todavía no se elige). Los días se descuentan completos del periodo
   * que contiene el primer día; si ese es otro, sus números no se tienen
   * y enseñar los de éste sería decir algo falso.
   */
  get saldoDelBorrador(): Saldo | null {
    const s = this.saldoDe(this.borrador.idUsuario);
    if (!s) {
      return null;
    }
    const f = this.borrador.fechaInicio;
    return !f || this.contiene(s, f) ? s : null;
  }

  /** El primer día cae en un periodo que no es el que está en pantalla. */
  get borradorFueraDePeriodo(): boolean {
    const s = this.saldoDe(this.borrador.idUsuario);
    const f = this.borrador.fechaInicio;
    return !!s && !!f && !this.contiene(s, f);
  }

  /**
   * De dónde se descuenta cuando el primer día cae fuera del periodo que
   * está en pantalla. No se bloquea: el servidor valida contra el periodo
   * correcto al guardar.
   *
   * No se calculan periodos aquí (la regla del aniversario y del 29 de
   * febrero vive en el servidor). Lo único seguro es que van encadenados:
   * el siguiente empieza justo en el fin de éste. Dónde TERMINA ese
   * siguiente no se adivina —con un ingreso del 29/02 el front lo ponía un
   * día antes y mandaba a «un periodo posterior» una vacación que era del
   * siguiente—, así que no se dice si es ése u otro más adelante.
   */
  get textoFueraDePeriodo(): string {
    const s = this.saldoDe(this.borrador.idUsuario);
    const f = this.borrador.fechaInicio;
    if (!s || !f || !s.periodoInicio || !s.periodoFin) {
      return "";
    }
    const fin = s.periodoFin.substring(0, 10);
    if (f >= fin) {
      return `Se descuenta de un periodo posterior (el siguiente empieza el ${this.fechaCompleta(fin)}); ` +
        "ese periodo no está en pantalla.";
    }
    return `Se descuenta de un periodo anterior al ${this.periodoTexto(s)}; ` +
      "ese periodo no está en pantalla.";
  }

  /**
   * Lo que le queda en el periodo del primer día, o null si no se sabe: no
   * es lo mismo "no le quedan" que "no sabemos cuántos tiene". Es la misma
   * cuenta de la tabla de Configuración (quedanDeSaldo).
   */
  get disponiblesDelBorrador(): number | null {
    return this.quedanDeSaldo(this.saldoDelBorrador);
  }

  /**
   * Los que le quedarían si se guardan estas vacaciones. Al corregir unas
   * que ya existen, sus propios días se devuelven primero, pero sólo si
   * también se descontaban de este mismo periodo: si antes caían en otro,
   * se devuelven a ese otro y aquí no suman.
   */
  get quedarianTras(): number | null {
    const disp = this.disponiblesDelBorrador;
    if (disp === null) {
      return null;
    }
    const mismoPeriodo =
      !!this.original && this.contiene(this.saldoDelBorrador, this.original.fechaInicio);
    const propios = this.editando && mismoPeriodo ? this.diasHabiles(this.original || {}) : 0;
    return disp + propios - this.diasDelBorrador;
  }

  get seExcede(): boolean {
    const q = this.quedarianTras;
    return q !== null && q < 0;
  }

  /* ---- Panel de días asignados ---- */

  /**
   * El periodo que se corrige en el modal, tal como lo mandó el servidor.
   * Sólo para ENSEÑAR (fechas, antigüedad, días por ley): lo que viaja al
   * guardar es borradorSaldo, que no lleva nada de esto.
   */
  saldoModal: Saldo | null = null;

  /** La fecha de ingreso con la que se abrió el modal, para saber si cambió. */
  private ingresoAlAbrir = "";

  /**
   * Abre el modal sobre el periodo que está en pantalla de esa persona.
   *
   * El `anio` es el DEL RENGLÓN —el año en que empieza su periodo—, nunca
   * el del mes que se ve: a quien cumple aniversario en diciembre, el
   * periodo vigente le empezó el año pasado, y con el del mes se habría
   * escrito en el periodo siguiente. Sin renglón («Asignar») no se manda:
   * el servidor usa su periodo vigente.
   */
  abrirSaldo(idUsuario?: number): void {
    // Los días propios el servidor los rechaza siempre: no se abre un modal
    // que sólo puede terminar en error. Un periodo cerrado ya no lo impide
    // (20/10/2026): en el modal se elige cuál corregir.
    const bloqueo = this.bloqueoPropio(idUsuario);
    if (bloqueo) {
      this.aviso(bloqueo, "warning");
      return;
    }
    this.borradorSaldo = { idUsuario: idUsuario };
    this.saldoAbierto = true;
    this.cargaSaldoModal(idUsuario);
  }

  /**
   * Los periodos de la persona del modal que se pueden corregir: el anterior
   * al vigente, el vigente y los que siguen, como los manda el servidor.
   * Norman (20/10/2026): «que se pueda editar el periodo para dejarlos
   * bien»; eligió escoger cuál corregir. Vacía mientras llegan.
   */
  periodosModal: Saldo[] = [];

  /** Llena el borrador con el periodo en pantalla de esa persona y trae sus periodos. */
  private cargaSaldoModal(idUsuario?: number): void {
    const s = idUsuario != null ? this.saldoDe(idUsuario) : null;
    this.periodosModal = [];
    this.llenaSaldoModal(s);
    this.borradorSaldo.fechaIngreso = s?.fechaIngreso || this.fechaIngresoDe(idUsuario);
    this.ingresoAlAbrir = this.borradorSaldo.fechaIngreso || "";
    if (idUsuario != null && this.saldoAbierto) {
      this.cargaPeriodosModal(idUsuario, s?.anio);
    }
  }

  /** El periodo que se corrige: de él salen los días, quién autoriza y la nota. */
  private llenaSaldoModal(s: Saldo | null): void {
    this.saldoModal = s;
    this.borradorSaldo.anio = s && s.anio != null ? s.anio : undefined;
    this.borradorSaldo.diasTotales = s ? s.diasTotales : undefined;
    this.borradorSaldo.area = s ? s.area : undefined;
    this.borradorSaldo.idAutoriza = s ? s.idAutoriza : undefined;
    this.borradorSaldo.comentario = s ? s.comentario : "";
  }

  /**
   * Trae sus periodos y deja elegido el de la tabla si está entre ellos (si
   * no, el vigente). Si mientras llegan se cerró el modal, se cambió de
   * persona o se escribió otra fecha de ingreso, no se toca nada.
   */
  private cargaPeriodosModal(idUsuario: number, anio?: number): void {
    this.service.periodosDe(idUsuario).subscribe({
      next: (res) => {
        if (!this.saldoAbierto || this.borradorSaldo.idUsuario !== idUsuario || res.codigoError) {
          return;
        }
        this.periodosModal = res.saldos || [];
        const elegido = this.periodosModal.find((p) => p.anio === anio)
          || this.periodosModal.find((p) => p.vigente === true)
          || this.periodosModal[0];
        if (elegido && !this.ingresoCambio) {
          this.llenaSaldoModal(elegido);
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.periodosModal = [];
        this.cdr.detectChanges();
      },
    });
  }

  /** Otro periodo en la lista: sus días, quién autoriza y su nota pasan al borrador. */
  eligePeriodoModal(anio: number): void {
    const p = this.periodosModal.find((x) => x.anio === anio);
    if (p) {
      this.llenaSaldoModal(p);
    }
  }

  /** «Del 18/12/2025 al 18/12/2026»: lo que enseña la lista cerrada (sólo las fechas, para que quepan). */
  textoFechas(s: Saldo | null): string {
    return s ? `Del ${this.fechaCompleta(s.periodoInicio)} al ${this.fechaCompleta(s.periodoFin)}` : "";
  }

  /** «Del 18/12/2025 al 18/12/2026 · vigente», para la lista de periodos del modal. */
  textoPeriodoModal(s: Saldo): string {
    return `${this.textoFechas(s)} · ${this.estadoPeriodoModal(s)}`;
  }

  /** vigente, terminó o siguiente; con «año calendario» delante si no va por aniversario. */
  private estadoPeriodoModal(s: Saldo): string {
    let estado = "siguiente";
    if (s.vigente) {
      estado = "vigente";
    } else if ((s.periodoFin || "").substring(0, 10) <= this.hoyIso) {
      estado = "terminó";
    }
    return s.porAniversario === false ? `año calendario · ${estado}` : estado;
  }

  /** Cómo va el periodo elegido, junto a la etiqueta «Periodo»: «vigente · 24 gozados · le quedan 2». */
  get resumenPeriodoModal(): string {
    const s = this.saldoModal;
    if (!s || this.periodosModal.length < 2 || (this.ingresoCambio && this.borradorSaldo.fechaIngreso)) {
      return "";
    }
    const quedan = quedanVista(s);
    const cuenta = quedan === null ? "sin días asignados" : `${gozadosVista(s)} gozados · le quedan ${quedan}`;
    return `${this.estadoPeriodoModal(s)} · ${cuenta}`;
  }

  /** La fecha de ingreso es de la persona: viene con el colaborador, tenga o no saldo ese año. */
  private fechaIngresoDe(idUsuario?: number): string | undefined {
    if (idUsuario == null) {
      return undefined;
    }
    const c = this.colaboradores.find((x) => x.idUsuario === idUsuario);
    return c && c.fechaIngreso ? c.fechaIngreso : undefined;
  }

  /**
   * Al cambiar de persona dentro del modal, lo de la anterior no se
   * hereda: la fecha de ingreso es un dato permanente de la persona y
   * habría quedado escrita en la equivocada. Tampoco su `anio`: otra
   * fecha de ingreso es otro periodo.
   */
  cambiaColaboradorSaldo(idUsuario?: number): void {
    this.cargaSaldoModal(idUsuario);
  }

  cerrarSaldo(): void {
    this.saldoAbierto = false;
    this.borradorSaldo = {};
    this.saldoModal = null;
    this.periodosModal = [];
    this.ingresoAlAbrir = "";
  }

  /* ---- Antigüedad ---- */

  /** Tope del campo de fecha de ingreso: hoy. */
  readonly hoyIso = (() => {
    const d = new Date();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const dd = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${m}-${dd}`;
  })();

  /** La fecha de ingreso del modal ya no es con la que se abrió. */
  get ingresoCambio(): boolean {
    return (this.borradorSaldo.fechaIngreso || "") !== this.ingresoAlAbrir;
  }

  /**
   * Al escribir OTRA fecha de ingreso los días de goce se dejan en blanco:
   * con ella cambian sus periodos y su antigüedad, y el servidor pone los
   * de ley del periodo que se guarda. La antigüedad ya no se calcula aquí
   * (la resta de años daba la del año calendario, no la del aniversario).
   *
   * Borrarla NO deja los días en blanco: en blanco se guardan como "sin
   * capturar" y se perderían los que tenía.
   */
  cambiaIngreso(): void {
    if (this.ingresoCambio && this.borradorSaldo.fechaIngreso) {
      this.borradorSaldo.diasTotales = undefined;
    } else if (!this.ingresoCambio) {
      // Volvió a la de antes: regresan los días que tenía.
      this.borradorSaldo.diasTotales = this.saldoModal ? this.saldoModal.diasTotales : undefined;
    }
  }

  /** "del 20/09/2026 al 20/09/2027", o qué periodo se va a crear. */
  get periodoModalTexto(): string {
    // Con una fecha de ingreso nueva el servidor NO escribe en el renglón que
    // se abrió (el del año calendario, o el del aniversario viejo): guarda los
    // días en el periodo de aniversario vigente hoy con la fecha nueva. Enseñar
    // las fechas del renglón sería decir un periodo y escribir en otro.
    if (this.ingresoCambio && this.borradorSaldo.fechaIngreso) {
      return "Su periodo de aniversario vigente (lo calcula COS al guardar con la fecha nueva)";
    }
    const s = this.saldoModal;
    if (!s || !s.periodoInicio || !s.periodoFin) {
      return "Su periodo vigente (lo calcula COS al guardar)";
    }
    const texto = `Del ${this.fechaCompleta(s.periodoInicio)} al ${this.fechaCompleta(s.periodoFin)}`;
    return s.porAniversario === false ? `${texto} · año calendario` : texto;
  }

  guardarSaldo(): void {
    if (!this.borradorSaldo.idUsuario) {
      this.aviso("Elige de quién son los días.", "warning");
      return;
    }
    // En el modal se puede cambiar de persona, y la que se elija puede ser
    // uno mismo. Se dice antes de ir al servidor, que lo rechazaría igual. El
    // periodo ya no estorba: sólo se ofrecen los que se pueden corregir.
    const bloqueo = this.bloqueoPropio(this.borradorSaldo.idUsuario);
    if (bloqueo) {
      this.aviso(bloqueo, "warning");
      return;
    }
    // Los días pueden quedar en blanco: nulo significa "todavía no se
    // capturan", que es como quedaron los que entraron por el
    // organigrama. Así se puede corregir quién autoriza sin tener que
    // inventarle un número de días. Con una fecha de ingreso nueva, en
    // blanco quiere decir otra cosa: que el servidor ponga los de ley.
    if (this.borradorSaldo.diasTotales != null && this.borradorSaldo.diasTotales < 0) {
      this.aviso("Los días no pueden ser negativos.", "warning");
      return;
    }
    if (this.borradorSaldo.idAutoriza === this.borradorSaldo.idUsuario) {
      this.aviso("Nadie puede autorizar sus propias vacaciones.", "warning");
      return;
    }
    this.guardandoSaldo = true;
    const { desde, hasta } = this.rangoVisible();
    const ingresoCambio = this.ingresoCambio;
    this.service.guardarSaldo(this.borradorSaldo, desde, hasta).subscribe({
      next: (res) => {
        this.guardandoSaldo = false;
        if (res.codigoError) {
          this.aviso(res.mensajeError || "No se pudo guardar.", "error");
          this.cdr.detectChanges();
          return;
        }
        // Con el rango visible el servidor contesta la MISMA lista que se
        // estaba viendo (los periodos vigentes a la misma fecha), ya con
        // el cambio: se pinta tal cual, sin volver a pedir el mes.
        this.aplicaSaldos(res.saldos);
        // La fecha de ingreso también vive en el colaborador (de ahí la
        // toma el modal de quien todavía no tiene periodo).
        const conIngreso = !!this.borradorSaldo.fechaIngreso;
        if (ingresoCambio) {
          const c = this.colaboradores.find((x) => x.idUsuario === this.borradorSaldo.idUsuario);
          if (c) {
            c.fechaIngreso = this.borradorSaldo.fechaIngreso || undefined;
          }
        }
        this.cerrarSaldo();
        this.aviso(
          ingresoCambio && conIngreso
            ? "Guardado. Sus periodos ya van de aniversario a aniversario de su fecha de ingreso."
            : "Días de vacaciones guardados.",
          "success",
        );
        this.cdr.detectChanges();
      },
      error: () => {
        this.guardandoSaldo = false;
        this.aviso("No se pudo guardar. Revisa tu conexión.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ===================== Fechas del formulario ===================== */

  /**
   * El datepicker solo deja elegir días hábiles: sábado y domingo no se
   * trabajan, así que no tiene sentido que sean el primer o el último día
   * de un periodo.
   */
  readonly soloHabiles = (d: Date | null): boolean => {
    if (!d) {
      return false;
    }
    const dia = d.getDay();
    return dia !== 0 && dia !== 6;
  };

  /** Del Date del datepicker al ISO que viaja al backend. */
  fechaDesdePicker(valor: Date | null, campo: "fechaInicio" | "fechaFin" | "fechaRegreso"): void {
    this.borrador[campo] = valor ? this.iso(valor) : "";

    // Se guarda el Date que entrego el datepicker, no uno reconstruido:
    // asi la referencia no cambia sola entre ciclos.
    if (campo === "fechaInicio") {
      this.fIni = valor;
      // Si el ultimo dia quedo antes del primero, se empareja.
      if (this.borrador.fechaFin && this.borrador.fechaFin < this.borrador.fechaInicio!) {
        this.borrador.fechaFin = this.borrador.fechaInicio;
        this.fFin = valor;
      }
      this.proponerRegreso();
      this.fReg = this.borrador.fechaRegreso ? this.aFecha(this.borrador.fechaRegreso) : null;
    } else if (campo === "fechaFin") {
      this.fFin = valor;
      this.proponerRegreso();
      this.fReg = this.borrador.fechaRegreso ? this.aFecha(this.borrador.fechaRegreso) : null;
    } else {
      this.fReg = valor;
    }
  }

  /** Del ISO guardado al Date que entiende el datepicker. */
  /** Las tres fechas del borrador, en Date, para los datepickers.
   *
   *  Tienen que vivir en propiedades y NO salir de una funcion llamada
   *  desde la plantilla: si el binding devuelve un Date nuevo cada vez,
   *  ngModel lo ve siempre como un valor distinto, se reescribe solo y
   *  la deteccion de cambios se queda dando vueltas. Cuando eso pasa la
   *  pantalla se cuelga entera y ningun boton vuelve a responder. */
  fIni: Date | null = null;
  fFin: Date | null = null;
  fReg: Date | null = null;

  /** Rearma los tres Date a partir del borrador. Se llama al abrir el
   *  panel, no en cada ciclo. */
  private sincronizaPickers(): void {
    this.fIni = this.borrador.fechaInicio ? this.aFecha(this.borrador.fechaInicio) : null;
    this.fFin = this.borrador.fechaFin ? this.aFecha(this.borrador.fechaFin) : null;
    this.fReg = this.borrador.fechaRegreso ? this.aFecha(this.borrador.fechaRegreso) : null;
  }

  /* ===================== La lista de periodos ===================== */

  /** ¿Este periodo ya termino? Se compara contra el dia de regreso. */
  yaRegreso(v: Vacacion): boolean {
    if (!v.fechaRegreso) {
      return false;
    }
    return v.fechaRegreso <= this.iso(new Date());
  }

  /**
   * Lo que se lista del mes: primero lo que sigue vigente o esta por
   * venir. Lo ya concluido se saca de aqui —para eso esta la bitacora—
   * pero NO se borra: el registro es justamente lo que alimenta el
   * conteo de dias tomados.
   */
  get periodosVigentes(): Vacacion[] {
    return this.vacaciones.filter((v) => !this.yaRegreso(v));
  }

  get periodosConcluidos(): Vacacion[] {
    return this.vacaciones.filter((v) => this.yaRegreso(v));
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.periodosVigentes.length / this.PORPAGINA));
  }

  /** Solo los de la pagina actual: la lista no crece hacia abajo. */
  get periodosPagina(): Vacacion[] {
    const desde = (this.pagina - 1) * this.PORPAGINA;
    return this.periodosVigentes.slice(desde, desde + this.PORPAGINA);
  }

  irPagina(n: number): void {
    if (n >= 1 && n <= this.totalPaginas) {
      this.pagina = n;
    }
  }

  get paginas(): number[] {
    const lista: number[] = [];
    for (let i = 1; i <= this.totalPaginas; i++) {
      lista.push(i);
    }
    return lista;
  }

  /* ===================== Bitácora ===================== */

  /** La frase del encabezado: qué hace la sección en la que se está. */
  get subPestana(): string {
    switch (this.pestana) {
      case "autorizaciones": return "Lo que espera tu respuesta: vacaciones y permisos.";
      case "periodos": return "Lo autorizado y lo que espera respuesta, ordenado por fecha.";
      case "seguimiento": return "Todo el año calendario con su rastro: autorizadas, no autorizadas y retiradas.";
      case "permisos": return "Quién falta, sale antes o llega tarde. El FRC-017.";
      case "config": return "El periodo de cada quien, de aniversario a aniversario de su Alta IMSS, como la hoja de RH.";
      default: return "Quién descansa y qué días. Solo días laborales.";
    }
  }

  verPestana(
    cual: "calendario" | "autorizaciones" | "periodos" | "seguimiento" | "permisos" | "config",
  ): void {
    this.pestana = cual;
    if (cual === "autorizaciones") {
      // Ya pintada la tabla se sabe dónde empieza y cuántos renglones caben.
      this.cdr.detectChanges();
      this.ajustaPaginaAut();
    }
    // Se recarga si nunca se cargo o si lo que hay es de otro anio: al
    // navegar meses se puede cruzar de anio y el acumulado seria el
    // equivocado.
    if (cual === "seguimiento" && this.anioCargado !== this.mes.getFullYear()) {
      this.cargarSeguimiento();
    }
  }

  /* ================= Vista de año ================= */

  /**
   * Mes o año dentro de la misma pestaña.
   *
   * No es una quinta pestaña a propósito: las dos vistas responden la
   * misma pregunta —quién descansa— con distinto alcance, y separarlas
   * en pestañas obligaba a recordar en cuál estabas.
   */
  vistaCalendario: "mes" | "anio" = "mes";

  readonly MESES_CORTOS = [
    "Ene", "Feb", "Mar", "Abr", "May", "Jun",
    "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
  ];

  verVista(v: "mes" | "anio"): void {
    this.vistaCalendario = v;
    // El año necesita TODO el año, no el rango del mes: es el mismo
    // dato que alimenta Seguimiento, así que se reusa su carga.
    if (v === "anio" && this.anioCargado !== this.mes.getFullYear()) {
      this.cargarSeguimiento();
    }
  }

  /** Días hábiles de un periodo que caen DENTRO de un mes concreto. */
  private habilesEnMes(v: Vacacion, anio: number, mes: number): number {
    if (!v.fechaInicio || !v.fechaFin) {
      return 0;
    }
    const desde = this.aFecha(v.fechaInicio);
    const hasta = this.aFecha(v.fechaFin);
    const cursor = new Date(Math.max(desde.getTime(), new Date(anio, mes, 1).getTime()));
    const fin = new Date(Math.min(hasta.getTime(), new Date(anio, mes + 1, 0).getTime()));
    let n = 0;
    while (cursor <= fin) {
      const d = cursor.getDay();
      if (d !== 0 && d !== 6 && !this.festivos.has(this.iso(cursor))) {
        n++;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return n;
  }

  /**
   * Una fila por persona con vacaciones ese año, y sus doce meses.
   *
   * Sólo lo que OCUPA días —autorizado y lo que espera respuesta—: una
   * solicitud negada o retirada no es una ausencia, y pintarla haría
   * creer que esa persona no está.
   */
  get filasAnio(): {
    idUsuario: number;
    nombre: string;
    color: string;
    meses: number[];
    total: number;
  }[] {
    const anio = this.mes.getFullYear();
    const porPersona = new Map<number, { nombre: string; color: string; meses: number[] }>();

    for (const v of this.delAnio) {
      if (v.estatus !== "ACTIVO" && v.estatus !== "PENDIENTE") {
        continue;
      }
      if (v.idUsuario == null) {
        continue;
      }
      let fila = porPersona.get(v.idUsuario);
      if (!fila) {
        fila = {
          nombre: v.colaborador || "",
          color: v.color || this.colorDe(v.idUsuario),
          meses: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        };
        porPersona.set(v.idUsuario, fila);
      }
      for (let m = 0; m < 12; m++) {
        fila.meses[m] += this.habilesEnMes(v, anio, m);
      }
    }

    return Array.from(porPersona.entries())
      .map(([idUsuario, f]) => ({
        idUsuario,
        nombre: f.nombre,
        color: f.color,
        meses: f.meses,
        total: f.meses.reduce((a, b) => a + b, 0),
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  }

  /** Cuánta gente sale cada mes. Es la fila que contesta "¿y diciembre?". */
  get personasPorMes(): number[] {
    const cuenta = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    for (const f of this.filasAnio) {
      for (let m = 0; m < 12; m++) {
        if (f.meses[m] > 0) {
          cuenta[m]++;
        }
      }
    }
    return cuenta;
  }

  /** El mes que se está viendo, para resaltarlo en la rejilla del año. */
  esMesActual(m: number): boolean {
    return this.mes.getMonth() === m;
  }

  /** De la rejilla del año al detalle de ese mes: es el gesto natural. */
  vaAlMes(m: number): void {
    this.mes = new Date(this.mes.getFullYear(), m, 1);
    this.vistaCalendario = "mes";
    this.cargar();
  }

  /**
   * Iniciales del nombre y del primer apellido.
   *
   * En celular la columna del día mide unos 46 px: el nombre completo
   * ahí no se recorta, se parte letra por letra ("Norma n Erick") y
   * queda ilegible. Con las iniciales se sabe quién es de un vistazo y
   * el nombre entero sigue estando al tocar.
   */
  iniciales(nombre?: string): string {
    const partes = (nombre || "").trim().split(/\s+/).filter((x) => x.length);
    if (!partes.length) {
      return "?";
    }
    const primera = partes[0][0];
    // El apellido PATERNO: con nombre compuesto («Norman Erick Estrada
    // Vargas») es la tercera palabra; con nombre simple («Emanuel López
    // Ramírez»), la segunda. Tomar siempre la última daría el materno.
    const apellido = partes.length >= 4 ? partes[2] : partes[1];
    return (primera + (apellido ? apellido[0] : "")).toUpperCase();
  }

  /** El color del colaborador, para cuando el periodo no lo trae. */
  private colorDe(idUsuario?: number): string {
    const c = this.colaboradores.find((x) => x.idUsuario === idUsuario);
    return (c && c.color) || "#8496a5";
  }

  /* ---------- Prima vacacional ---------- */

  /**
   * La prima en DÍAS DE SALARIO, no en pesos: el 25% que marca la LFT
   * (art. 80) sobre los días de goce de su periodo. Lo acumulado de
   * periodos anteriores no la sube.
   *
   * En pesos no se puede y no se inventa: COS no guarda sueldos (la
   * tabla `nomina` existe pero está vacía). Nómina multiplica esto por
   * el salario diario y le sale el monto. Se calcula sobre los días de
   * goce, que es como se paga aquí; si algún día se decide pagarla sobre
   * los días efectivamente gozados, es cambiar totalesDe por
   * gozadosDe(saldoDe(...)) en esta única línea.
   */
  primaDe(idUsuario?: number): number | null {
    const dias = this.totalesDe(idUsuario);
    return dias === null ? null : Math.round(dias * 0.25 * 100) / 100;
  }

  /** La barra del mes solo aplica a lo que es del mes. */
  /**
   * El navegador del mes solo donde el mes significa algo.
   *
   * En Configuración se ven periodos de vacaciones, no meses: enseñar ahí
   * un selector de mes hacía creer que los días cambian de un mes a otro.
   * Sus periodos son los vigentes a `fechaSaldos` (sale del último mes que
   * se vio) y la tabla lo dice arriba. En Seguimiento tampoco, que es el
   * año calendario completo.
   */
  get mostrarBarraMes(): boolean {
    // En la vista de año el selector de mes no significa nada.
    return (
      (this.pestana === "calendario" && this.vistaCalendario === "mes") ||
      this.pestana === "periodos"
    );
  }

  /** El interruptor Mes/Año sólo existe donde hay dos vistas. */
  get mostrarVistas(): boolean {
    return this.pestana === "calendario";
  }

  /**
   * Tras guardar o quitar, el acumulado quedo viejo. Si la bitacora esta
   * a la vista se recarga en el momento; si no, se marca como pendiente
   * para que se recargue al entrar. Vaciarla sin mas la dejaba en blanco
   * cuando el usuario ya estaba parado en esa pestaña.
   */
  private refrescarBitacora(): void {
    this.anioCargado = null;
    if (this.pestana === "seguimiento") {
      this.cargarSeguimiento();
    } else {
      this.delAnio = [];
    }
  }

  /** Todo el anio visible, para acumular los dias por colaborador. */
  /**
   * Trae TODO el año, con rechazadas y canceladas incluidas.
   *
   * No usa consultar(): ese filtra a lo vigente y a lo que espera, y
   * aquí la pregunta es qué pasó con cada petición — una que se negó es
   * justo lo que hay que poder revisar después.
   */
  cargarSeguimiento(): void {
    const anio = this.mes.getFullYear();
    this.cargandoBitacora = true;
    this.service.historial(anio).subscribe({
      next: (res) => {
        this.cargandoBitacora = false;
        this.delAnio = res.codigoError ? [] : res.vacaciones || [];
        this.anioCargado = res.codigoError ? null : anio;
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargandoBitacora = false;
        this.anioCargado = null;
        this.cdr.detectChanges();
      },
    });
  }

  /* ---------- Seguimiento ---------- */

  /** Filtros de la pestaña. Vacío quiere decir "todo". */
  segEstatus = "";
  segArea = "";
  segTexto = "";

  /** Los estatus que existen, con su etiqueta y su color. */
  readonly ESTATUS_SEG = [
    { valor: "PENDIENTE", texto: "Esperando", color: "#b45309" },
    { valor: "ACTIVO", texto: "Autorizada", color: "#15803d" },
    { valor: "RECHAZADA", texto: "No autorizada", color: "#b91c1c" },
    { valor: "CANCELADO", texto: "Retirada", color: "#64748b" },
  ];

  etiquetaEstatus(e?: string): string {
    const x = this.ESTATUS_SEG.find((s) => s.valor === e);
    return x ? x.texto : e || "";
  }

  colorEstatus(e?: string): string {
    const x = this.ESTATUS_SEG.find((s) => s.valor === e);
    return x ? x.color : "#64748b";
  }

  /**
   * Lo del año pasado por los tres filtros.
   *
   * Ya viene ordenado del servidor —lo más reciente primero— así que
   * aquí solo se descarta, no se reordena: reordenar en pantalla
   * escondería el criterio real y confundiría al comparar con la base.
   */
  /* ---- Paginado del seguimiento: la tabla no crece hacia abajo ---- */

  porPaginaSeg = 10;
  paginaSeg = 1;

  get totalPaginasSeg(): number {
    return Math.max(1, Math.ceil(this.seguimiento.length / this.porPaginaSeg));
  }

  /**
   * Se acota aquí y no corrigiendo paginaSeg: los filtros cambian la
   * cuenta sin avisar, y escribir estado desde un getter dispara el
   * error de "expresión cambió después de revisarse".
   */
  private get paginaSegValida(): number {
    return Math.min(Math.max(1, this.paginaSeg), this.totalPaginasSeg);
  }

  get seguimientoPagina(): Vacacion[] {
    const desde = (this.paginaSegValida - 1) * this.porPaginaSeg;
    return this.seguimiento.slice(desde, desde + this.porPaginaSeg);
  }

  get desdeSeg(): number {
    return this.seguimiento.length ? (this.paginaSegValida - 1) * this.porPaginaSeg + 1 : 0;
  }

  get hastaSeg(): number {
    return Math.min(this.paginaSegValida * this.porPaginaSeg, this.seguimiento.length);
  }

  irPaginaSeg(n: number): void {
    if (n >= 1 && n <= this.totalPaginasSeg) {
      this.paginaSeg = n;
    }
  }

  /** Ventana de páginas: primera, última y las vecinas; 0 es un hueco (…). */
  get paginasSeg(): number[] {
    const total = this.totalPaginasSeg;
    const act = this.paginaSegValida;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const lista: number[] = [1];
    if (act > 3) {
      lista.push(0);
    }
    for (let n = Math.max(2, act - 1); n <= Math.min(total - 1, act + 1); n++) {
      lista.push(n);
    }
    if (act < total - 2) {
      lista.push(0);
    }
    lista.push(total);
    return lista;
  }

  get seguimiento(): Vacacion[] {
    const q = this.segTexto.trim().toLowerCase();
    return this.solicitudesDelAnio.filter((v) => {
      if (this.segEstatus && v.estatus !== this.segEstatus) {
        return false;
      }
      if (this.segArea && this.areaDe(v.idUsuario) !== this.segArea) {
        return false;
      }
      if (q) {
        const nombre = (v.colaborador || "").toLowerCase();
        const motivo = (v.motivo || "").toLowerCase();
        const nota = this.nota(v).toLowerCase();
        if (!nombre.includes(q) && !motivo.includes(q) && !nota.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }

  /** Cuántas hay de cada estatus, para las pastillas del filtro. */
  cuantasCon(estatus: string): number {
    return this.solicitudesDelAnio.filter((v) => v.estatus === estatus).length;
  }

  /** Las áreas que aparecen en lo del año, para el select. */
  get areasSeguimiento(): string[] {
    const vistas = new Set<string>();
    for (const v of this.delAnio) {
      const a = this.areaDe(v.idUsuario);
      if (a) {
        vistas.add(a);
      }
    }
    return Array.from(vistas).sort((a, b) => a.localeCompare(b, "es"));
  }

  limpiaFiltrosSeg(): void {
    this.segEstatus = "";
    this.segArea = "";
    this.segTexto = "";
  }

  get hayFiltrosSeg(): boolean {
    return !!(this.segEstatus || this.segArea || this.segTexto.trim());
  }

  /** Quién resolvió y cuándo, en una línea. */
  resolucion(v: Vacacion): string {
    if (v.estatus === "PENDIENTE") {
      return "";
    }
    const quien = v.autorizaNombre || "";
    const cuando = v.fechaResolucion ? v.fechaResolucion.substring(0, 10) : "";
    if (!quien && !cuando) {
      return "";
    }
    return quien ? `${quien}${cuando ? " · " + this.bonita(cuando) : ""}` : this.bonita(cuando);
  }

  /**
   * Baja lo que se está viendo como CSV.
   *
   * Se exporta lo FILTRADO y no todo el año: quien acaba de filtrar por
   * un área espera bajar eso, no 300 renglones.
   */
  exportaSeguimiento(): void {
    const filas = this.seguimiento;
    if (!filas.length) {
      this.aviso("No hay nada que exportar con esos filtros.", "warning");
      return;
    }

    // «Documento» (15/10/2026): RH audita con el Excel en la mano y ahí
    // tiene que decir si hay FRC-019 y, si se subió uno, cuándo y quién.
    const cab = [
      "Colaborador", "Área", "Desde", "Hasta", "Regresa", "Días",
      "Estatus", "Origen", "Pedida", "Resolvió", "Fecha resolución",
      "Motivo", "Comentario", "Documento",
    ];

    // Las comillas dentro de un campo se duplican, que es como se
    // escapan en CSV; si no, Excel parte el renglón donde no debe.
    const esc = (x: unknown) => `"${String(x == null ? "" : x).replaceAll('"', '""')}"`;

    const lineas = [cab.map(esc).join(",")];
    for (const v of filas) {
      lineas.push([
        v.colaborador, this.areaDe(v.idUsuario), v.fechaInicio, v.fechaFin,
        v.fechaRegreso, this.diasHabiles(v), this.etiquetaEstatus(v.estatus),
        v.origen === "SOLICITUD" ? "Pedida" : "Capturada",
        v.fechaCreacion, v.autorizaNombre, v.fechaResolucion,
        v.motivo, this.nota(v),
        textoDocumento(vacacionConFrc(v), v.enPapel, v.firmado),
      ].map(esc).join(","));
    }

    // El BOM va primero o Excel abre los acentos como jeroglíficos.
    const csv = "\uFEFF" + lineas.join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    // El nombre dice que es el año CALENDARIO: los saldos van por periodo
    // de aniversario y un "vacaciones-2026" se confundiría con ellos.
    a.download = `vacaciones-${this.mes.getFullYear()}-anio-calendario.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * El año CALENDARIO de Seguimiento y de la vista Año (enero a
   * diciembre). No es el de ningún periodo: esos van de aniversario a
   * aniversario y viven en Configuración.
   */
  get anioSeguimiento(): number {
    return this.mes.getFullYear();
  }

  get totalDiasAnio(): number {
    return this.seguimiento.reduce((n, v) => n + this.diasHabiles(v), 0);
  }

  /* ===================== Alta y edición ===================== */

  abrirAlta(iso?: string | null): void {
    this.editando = null;
    this.original = null;
    this.borrador = {
      idUsuario: undefined,
      fechaInicio: iso || "",
      fechaFin: iso || "",
      fechaRegreso: "",
      comentario: "",
      color: this.COLORES[0],
    };
    this.sincronizaPickers();
    this.panelAbierto = true;
  }

  /** Mismo panel, con los datos cargados. Se trabaja sobre una copia:
   *  si se cancela, el periodo no queda tocado. */
  editar(v: Vacacion): void {
    this.editando = v.idVacacion || null;
    this.original = { ...v };
    this.borrador = {
      idVacacion: v.idVacacion,
      idUsuario: v.idUsuario,
      fechaInicio: v.fechaInicio,
      fechaFin: v.fechaFin,
      fechaRegreso: v.fechaRegreso,
      comentario: v.comentario,
      color: v.color || this.COLORES[0],
    };
    this.sincronizaPickers();
    this.panelAbierto = true;
  }

  cerrarAlta(): void {
    this.panelAbierto = false;
    this.editando = null;
    this.original = null;
    this.fIni = this.fFin = this.fReg = null;
    this.borrador = {};
  }

  /** Al elegir colaborador se propone el color que ya venia usando. */
  alElegirColaborador(): void {
    const c = this.colaboradores.find((x) => x.idUsuario === this.borrador.idUsuario);
    if (c && c.color) {
      this.borrador.color = c.color;
    }
  }

  /* ---- Selectores de persona con buscador (15/10/2026) ----
     Irving: el Colaborador de «Agregar/Editar vacaciones» era un <select>
     nativo con la lista inmensa y sin búsqueda. Los tres de persona (ése y,
     en el modal de días, de quién son y quién autoriza) son ahora un
     mat-select con buscador, como en Parámetros del BSC, los tres con la
     misma plantilla (#selectorPersona). Qué opciones salen lo decide
     ../filtro-personas; aquí, lo escrito en cada uno y a dónde va lo elegido. */

  /** Los colaboradores como opciones, en orden alfabético. Se arman en cargar(). */
  private personas: OpcionPersona[] = [];

  /** Lo escrito en cada buscador y las opciones que salen con eso. */
  readonly buscadores: Record<CampoPersona, { filtro: string; opciones: OpcionPersona[] }> = {
    colaborador: { filtro: "", opciones: [] },
    saldoQuien: { filtro: "", opciones: [] },
    saldoAutoriza: { filtro: "", opciones: [] },
  };

  /** Lo que dice cada campo sin nadie elegido, y su buscador. */
  readonly TEXTOS_PERSONA: Record<CampoPersona, { vacio: string; buscar: string }> = {
    colaborador: { vacio: "Selecciona…", buscar: "Buscar colaborador" },
    saldoQuien: { vacio: "Selecciona…", buscar: "Buscar colaborador" },
    saldoAutoriza: { vacio: "Nadie en particular", buscar: "Buscar quién autoriza" },
  };

  /** Quién está elegido en ese campo: el valor del select, y se queda en la lista aunque no coincida. */
  personaElegida(campo: CampoPersona): number | undefined {
    if (campo === "colaborador") {
      return this.borrador.idUsuario;
    }
    return campo === "saldoQuien" ? this.borradorSaldo.idUsuario : this.borradorSaldo.idAutoriza;
  }

  /**
   * Lo elegido en un selector, a su borrador. Lo de antes de cada campo
   * sigue igual: el colaborador del alta propone su color y el del modal
   * carga su periodo. Al quitar a quien autoriza, Material manda null: se
   * guarda undefined, que es lo que viajaba con el <select> de antes.
   */
  eligePersona(campo: CampoPersona, id: number | null | undefined): void {
    const valor = id ?? undefined;
    if (campo === "colaborador") {
      this.borrador.idUsuario = valor;
      this.alElegirColaborador();
    } else if (campo === "saldoQuien") {
      this.borradorSaldo.idUsuario = valor;
      this.cambiaColaboradorSaldo(valor);
    } else {
      this.borradorSaldo.idAutoriza = valor;
    }
  }

  /** Lo escrito en un buscador (llega "" cuando el buscador se limpia al cerrar). */
  buscaPersona(campo: CampoPersona, texto: string): void {
    const b = this.buscadores[campo];
    b.filtro = texto;
    b.opciones = filtraPersonas(this.personas, texto, this.personaElegida(campo));
  }

  /**
   * Al abrir, la lista se vuelve a armar: lo elegido pudo cambiar desde la
   * última vez (otra persona en el modal, otro periodo en el panel). Al
   * cerrar con Esc o con clic fuera, el foco regresa al campo y no se
   * pierde con el buscador.
   */
  alAbrirPersona(campo: CampoPersona): void {
    this.buscaPersona(campo, this.buscadores[campo].filtro);
  }

  alCerrarPersona(select: MatSelect): void {
    select.focus();
  }

  /** Cambió la lista de colaboradores (se pide con cada mes): se rearman los tres. */
  private armaPersonas(): void {
    this.personas = opcionesDePersonas(this.colaboradores);
    for (const campo of CAMPOS_PERSONA) {
      this.buscaPersona(campo, this.buscadores[campo].filtro);
    }
  }

  trackPersona(_: number, op: OpcionPersona): number {
    return op.idUsuario;
  }

  /**
   * Quien está elegido en el campo pero no viene en la lista: colaboradores()
   * solo trae a los empleados activos, y un saldo puede seguir con un autorizador dado
   * de baja o un periodo ser de alguien inactivo. Sin una opción con ese id
   * el select quedaría vacío y diría «Nadie en particular» o «Selecciona…»
   * aunque se guarde ese id. Se pinta en la plantilla, también con el
   * buscador escrito, para que el select no se vacíe al filtrar.
   */
  personaFuera(campo: CampoPersona): OpcionPersona | null {
    const id = this.personaElegida(campo);
    if (id == null || this.personas.some((p) => p.idUsuario === id)) {
      return null;
    }
    const nombre = (this.nombreFuera(campo) ?? "").trim() || `Usuario ${id}`;
    return { idUsuario: id, nombre: `${nombre} (inactivo)`, clave: "" };
  }

  /** El nombre que ya trae el registro para quien no está en la lista. */
  private nombreFuera(campo: CampoPersona): string | undefined {
    if (campo === "colaborador") {
      return this.original?.colaborador;
    }
    return campo === "saldoQuien" ? this.saldoModal?.colaborador : this.saldoModal?.autorizaNombre;
  }

  /**
   * Al mover la fecha de fin se propone el regreso: el siguiente día
   * hábil. Se puede corregir a mano.
   */
  proponerRegreso(): void {
    if (!this.borrador.fechaFin) {
      return;
    }
    const d = this.aFecha(this.borrador.fechaFin);
    do {
      d.setDate(d.getDate() + 1);
    } while (d.getDay() === 0 || d.getDay() === 6);
    this.borrador.fechaRegreso = this.iso(d);

    if (!this.borrador.fechaInicio) {
      this.borrador.fechaInicio = this.borrador.fechaFin;
    }
  }

  get diasDelBorrador(): number {
    return this.diasHabiles(this.borrador);
  }

  guardar(): void {
    if (!this.borrador.idUsuario) {
      this.aviso("Elige de quién son las vacaciones.", "warning");
      return;
    }
    if (!this.borrador.fechaInicio || !this.borrador.fechaFin) {
      this.aviso("Indica el primer y el último día de descanso.", "warning");
      return;
    }
    if (this.borrador.fechaFin < this.borrador.fechaInicio) {
      this.aviso("La fecha de fin no puede ser anterior a la de inicio.", "warning");
      return;
    }
    if (this.diasDelBorrador === 0) {
      this.aviso(
        "Ese periodo solo cubre sábado y domingo, que no se trabajan.",
        "warning",
      );
      return;
    }
    // El backend lo vuelve a comprobar; esto es para no mandar el viaje
    // en balde y poder decirlo con el número exacto. Sólo se frena cuando
    // el primer día cae en el periodo que está en pantalla: si cae en
    // otro, seExcede es falso y valida el servidor, que sí lo tiene.
    if (this.seExcede) {
      const disp = this.disponiblesDelBorrador || 0;
      this.aviso(
        `Solo le quedan ${disp} ${disp === 1 ? "día" : "días"} en su periodo ` +
          `${this.periodoTexto(this.saldoDelBorrador)} y estas vacaciones son ` +
          `${this.diasDelBorrador} ${this.diasDelBorrador === 1 ? "día" : "días"}.`,
        "warning",
      );
      return;
    }

    this.guardando = true;
    this.service.guardar(this.borrador).subscribe({
      next: (res) => {
        this.guardando = false;
        if (res.codigoError) {
          this.aviso(res.mensajeError || "No se pudo guardar.", "error");
          this.cdr.detectChanges();
          return;
        }
        const eraEdicion = this.editando !== null;
        this.cerrarAlta();
        this.aviso(eraEdicion ? "Periodo actualizado." : "Vacaciones registradas.", "success");
        this.refrescarBitacora();
        this.cargar();
      },
      error: () => {
        this.guardando = false;
        this.aviso("No se pudo guardar. Revisa tu conexión.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  quitar(v: Vacacion): void {
    // Los días vuelven al periodo que contiene su primer día. Si es el que
    // está en pantalla se nombra; si no, se dice sin inventar fechas.
    const s = this.saldoDe(v.idUsuario);
    const destino = this.contiene(s, v.fechaInicio)
      ? `a su periodo ${this.periodoTexto(s)}`
      : "al periodo de vacaciones en el que cae su primer día";
    swal
      .fire({
        title: "¿Quitar estas vacaciones?",
        html:
          `<div style="text-align:left;line-height:1.7;font-size:15px">` +
          `<div style="display:flex;align-items:center;gap:8px">` +
          `<span style="width:10px;height:10px;border-radius:50%;` +
          `background:${v.color || "#0369a1"};display:inline-block"></span>` +
          `<b>${v.colaborador || "El colaborador"}</b></div>` +
          `<div style="margin-top:6px">${this.textoRango(v)}</div>` +
          `<div style="margin-top:10px;padding-top:10px;` +
          `border-top:1px solid #e6ebf2;color:#64748b;font-size:13.5px">` +
          `Se le devuelven <b>${this.diasHabiles(v)}</b> ` +
          `${this.diasHabiles(v) === 1 ? "día" : "días"} ${destino}. ` +
          `Esta acción no se puede deshacer.</div></div>`,
        icon: "warning",
        showCancelButton: true,
        reverseButtons: true,
        focusCancel: true,
        confirmButtonText: "Sí, quitar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#b71c1c",
      })
      .then((r) => {
        if (!r.isConfirmed || v.idVacacion == null) {
          return;
        }
        this.service.eliminar(v.idVacacion).subscribe({
          next: (res) => {
            if (res.codigoError) {
              this.aviso(res.mensajeError || "No se pudo quitar.", "error");
              this.cdr.detectChanges();
              return;
            }
            this.aviso("Periodo quitado.", "success");
            this.refrescarBitacora();
            this.cargar();
          },
          error: () => {
            this.aviso("No se pudo quitar. Revisa tu conexión.", "error");
            this.cdr.detectChanges();
          },
        });
      });
  }

  /* ===================== Apoyo ===================== */

  /** El comentario, sin "NA", "N/A", "no aplica" ni compañía. */
  nota(v: Vacacion): string {
    return notaVisible(v.comentario);
  }

  /**
   * Lo que se lee al pasar el cursor sobre un día de vacaciones. Va en
   * varias líneas: quién, cuándo, cuántos días y quién lo capturó.
   */
  tipVacacion(v: Vacacion): string {
    const lineas: string[] = [v.colaborador || "Colaborador"];
    if (v.fechaInicio && v.fechaFin) {
      lineas.push(
        v.fechaInicio === v.fechaFin
          ? this.bonita(v.fechaInicio)
          : `Del ${this.bonita(v.fechaInicio)} al ${this.bonita(v.fechaFin)}`,
      );
    }
    const d = this.diasHabiles(v);
    lineas.push(`${d} ${d === 1 ? "día hábil" : "días hábiles"} de descanso`);
    if (v.fechaRegreso) {
      lineas.push(`Regresa el ${this.bonita(v.fechaRegreso)}`);
    }
    const nota = this.nota(v);
    if (nota) {
      lineas.push(`“${nota}”`);
    }
    return lineas.join("\n");
  }

  /** El de quien vuelve a trabajar ese día. */
  tipRegreso(v: Vacacion): string {
    const lineas: string[] = [v.colaborador || "Colaborador"];
    lineas.push("Primer día de vuelta al trabajo");
    if (v.fechaInicio && v.fechaFin) {
      lineas.push(
        `Descansó del ${this.bonita(v.fechaInicio)} al ${this.bonita(v.fechaFin)}`,
      );
    }
    return lineas.join("\n");
  }

  textoRango(v: Vacacion): string {
    // Días sueltos: cada tramo, sin el regreso (se va y se regresa varias veces).
    if (v.tramos && v.tramos.length > 1) {
      const textos = v.tramos.map((t) => this.rangoDe(t));
      return `${textos.slice(0, -1).join(", ")} y ${textos.at(-1)}`;
    }
    const base = this.rangoDe(v);
    return base && v.fechaRegreso ? `${base} · regresa el ${this.bonita(v.fechaRegreso)}` : base;
  }

  /** «2 de noviembre al 4 de noviembre», o un solo día. */
  private rangoDe(v: Vacacion): string {
    if (!v.fechaInicio || !v.fechaFin) {
      return "";
    }
    const ini = this.bonita(v.fechaInicio);
    return v.fechaInicio === v.fechaFin ? ini : `${ini} al ${this.bonita(v.fechaFin)}`;
  }

  /** "2 de septiembre" — para leerlo, no para capturarlo. */
  bonita(iso: string): string {
    const d = this.aFecha(iso);
    return `${d.getDate()} de ${this.nombresMes[d.getMonth()].toLowerCase()}`;
  }

  /** Fecha ISO a Date LOCAL. new Date("2026-09-02") la interpreta en UTC
   *  y en México se corre un día hacia atrás. */
  /* ================= Autorizar ================= */

  /** Días hábiles de una solicitud, para enseñarlos en la tarjeta. */
  diasDeSolicitud(v: Vacacion): number {
    return this.diasHabiles(v);
  }

  autorizar(v: Vacacion): void {
    if (v.idVacacion == null) {
      return;
    }

    const dias = this.diasHabiles(v);
    swal
      .fire({
        title: "¿Autorizar estas vacaciones?",
        html:
          `<div style="text-align:left;line-height:1.7;font-size:15px">` +
          `<b>${v.colaborador || "El colaborador"}</b>` +
          `<div style="margin-top:6px">${this.textoRango(v)}</div>` +
          `<div style="margin-top:10px;padding-top:10px;border-top:1px solid #e6ebf2;` +
          `color:#64748b;font-size:13.5px">Son <b>${dias}</b> ` +
          `${dias === 1 ? "día hábil" : "días hábiles"}. ` +
          `Al confirmar sigue tu firma — digital o en papel — que sella el FRC-019, y se pintan en el calendario.</div></div>`,
        icon: "question",
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Sí, autorizar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#15803d",
      })
      .then((r) => {
        if (r.isConfirmed) {
          // Una solicitud pedida antes de la firma electrónica no tiene
          // documento que sellar: se autoriza sin firmar.
          if (v.tieneFirma) {
            this.abreFirma(v);
          } else {
            this.resolver(v, true);
          }
        }
      });
  }

  rechazar(v: Vacacion): void {
    if (v.idVacacion == null) {
      return;
    }

    // El motivo es obligatorio: el colaborador lo va a leer y "no" a
    // secas no le sirve de nada para reacomodar sus fechas.
    swal
      .fire({
        title: "¿Por qué no se autoriza?",
        html:
          `<div style="text-align:left;font-size:14px;color:#64748b">` +
          `<b style="color:#253042">${v.colaborador || "El colaborador"}</b> · ` +
          `${this.textoRango(v)}</div>`,
        input: "textarea",
        inputPlaceholder: "Ej. Esa semana cierra el mes y no hay quien cubra.",
        inputAttributes: { maxlength: "300" },
        icon: "warning",
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "No autorizar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#b71c1c",
        inputValidator: (valor) =>
          !valor || !valor.trim() ? "Hay que escribir el motivo." : null,
      })
      .then((r) => {
        if (r.isConfirmed) {
          this.resolver(v, false, r.value as string);
        }
      });
  }

  private resolver(v: Vacacion, aprobar: boolean, motivo?: string, firma?: string): void {
    this.resolviendo = v.idVacacion || null;
    this.service.resolver(v.idVacacion!, aprobar, motivo, firma).subscribe({
      next: (res) => {
        this.resolviendo = null;
        if (res.codigoError) {
          this.aviso(res.mensajeError || "No se pudo resolver.", "error");
          this.cdr.detectChanges();
          return;
        }
        // Firma en papel: se descarga el FRC-019 con la línea en blanco.
        if (aprobar && firma === FirmaFrcComponent.FISICA) {
          this.descargaFrc(v.idVacacion!);
        }
        this.aviso(
          aprobar ? "Autorizada. Ya se ve en el calendario." : "Quedó como no autorizada.",
          "success"
        );
        // Se recarga todo: al autorizar cambia el calendario, el saldo y
        // la lista de pendientes a la vez.
        this.cargar();
        this.refrescarBitacora();
      },
      error: () => {
        this.resolviendo = null;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ================= La firma y el FRC-019 ================= */

  /**
   * Quien administra también autoriza desde aquí, así que también
   * firma aquí (app-firma-frc): digital o descargando el FRC-019 para
   * firmarlo en papel. Un rechazo no se firma.
   */
  firmaAbierta = false;
  firmaObjetivo: Vacacion | null = null;
  generandoFrc: number | null = null;

  abreFirma(v: Vacacion): void {
    this.firmaObjetivo = v;
    this.firmaAbierta = true;
    // Se llega desde el .then del swal, fuera del ciclo de Angular:
    // sin esto el modal no se pinta.
    this.cdr.detectChanges();
  }

  cierraFirma(): void {
    this.firmaAbierta = false;
    this.firmaObjetivo = null;
    this.cdr.detectChanges();
  }

  /** Lo que emite el modal: el PNG trazado, o FISICA si se firmará en papel. */
  firmada(firma: string): void {
    const objetivo = this.firmaObjetivo;
    this.cierraFirma();
    if (objetivo) {
      this.resolver(objetivo, true, undefined, firma);
    }
  }

  frcDe(v: Vacacion): void {
    if (v.idVacacion != null) {
      this.descargaFrc(v.idVacacion);
    }
  }

  /* ---- El documento de cada solicitud (15/10/2026) ----
     Irving: RH audita, así que ve, baja y carga el documento de cada
     solicitud, no sólo el líder y quien la pidió. Quien usa este calendario
     lo administra, o sea que es RH. Sale en Periodos vigentes, en
     Seguimiento y en Autorizaciones, con las reglas de Seguimiento RH
     (../frc-firmado/documento-rh); el servidor las vuelve a comprobar. */
  readonly vacacionConFrc = vacacionConFrc;
  readonly rhCargaVacacion = rhCargaVacacion;
  readonly rhCargaPermiso = rhCargaPermiso;

  /** Ni FRC-019, ni nada subido, ni se le puede cargar: la celda dice «—». */
  sinDocumento(v: Vacacion): boolean {
    return !vacacionConFrc(v) && !v.firmado?.idFirmado && !rhCargaVacacion(v, this.miId);
  }

  /** Pide los datos con firmas y arma el PDF. */
  private descargaFrc(idVacacion: number): void {
    this.generandoFrc = idVacacion;
    this.service.frc(idVacacion).subscribe({
      next: (res) => {
        this.generandoFrc = null;
        const dato = (res.vacaciones || [])[0];
        if (res.codigoError || !dato) {
          this.aviso(res.mensajeError || "No se pudo armar el FRC-019.", "error");
          this.cdr.detectChanges();
          return;
        }
        generaFrc019(dato, (t) => this.diasHabiles(t));
        this.cdr.detectChanges();
      },
      error: () => {
        this.generandoFrc = null;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ================= Configuración ================= */

  /* ---------- Lo que no se corrige desde aquí ----------
   *
   * El servidor (VacacionBuss.guardarSaldo) rechaza SIEMPRE corregirse a uno
   * mismo y, desde el 20/10/2026, sólo un periodo más viejo que el anterior
   * al vigente. El lápiz abre el modal, donde se elige cuál corregir; la
   * captura rápida de la celda trabaja sobre el periodo que se ve, y si ya
   * terminó lo manda al lápiz. La tabla no debe invitar a algo que siempre
   * va a fallar. */

  /**
   * El id de quien está dentro, del CosData que deja el login (el mismo
   * patrón de Solicitudes IA: usuario.id siempre, idUsuarioSession desde el
   * WAR del 22/09). Sólo sirve para no ofrecer lo que el servidor va a
   * rechazar; él lo vuelve a comprobar, así que un CosData viejo sólo puede
   * apagar un botón de más, nunca dejar cambiar lo propio. Desde el
   * 15/10/2026 también lo lee la plantilla: el documento de lo propio no se
   * ofrece cargar (lo carga otra persona que administre), y la lectura vive
   * en ../frc-firmado/sesion, la misma del Seguimiento RH.
   */
  readonly miId: number | null = idEnSesion();

  /**
   * Por qué los días de esa persona no se pueden corregir con el lápiz, o ""
   * si sí se puede: sólo los propios. Es el tooltip del lápiz y el aviso si
   * aun así se le da clic (en un teléfono el tooltip no sale al tocar).
   */
  bloqueoPropio(idUsuario?: number): string {
    if (idUsuario == null || this.miId == null || idUsuario !== this.miId) {
      return "";
    }
    return "Tus propios días no los puedes cambiar tú: que lo haga otra persona que administre el calendario.";
  }

  /**
   * Por qué la captura rápida de la celda no se puede usar, o "" si sí: los
   * días propios, o un periodo que ya terminó (ese se corrige con el lápiz,
   * eligiéndolo en «Periodo», si es el anterior al vigente).
   */
  bloqueoSaldo(idUsuario?: number): string {
    const propio = this.bloqueoPropio(idUsuario);
    if (propio) {
      return propio;
    }
    if (idUsuario != null && this.estadoPeriodo(idUsuario) === "CERRADO") {
      return "Ese periodo ya terminó. Para corregir su periodo anterior, ábrelo con el lápiz y elígelo en «Periodo».";
    }
    return "";
  }

  /* ---------- Captura de días en la propia celda ---------- */

  /** Quién se está editando. null cuando no hay nada abierto. */
  editandoDias: number | null = null;
  borradorDias: number | null = null;
  guardandoDias: number | null = null;

  /** Abre la celda. Se llega con clic o con Enter desde el teclado. */
  editaDias(c: Colaborador): void {
    if (c.idUsuario == null) {
      return;
    }
    // La celda va apagada con aria-disabled (no [disabled], para que salga
    // el tooltip), así que el clic llega: aquí se detiene y se dice por qué.
    const bloqueo = this.bloqueoSaldo(c.idUsuario);
    if (bloqueo) {
      this.aviso(bloqueo, "warning");
      return;
    }
    this.editandoDias = c.idUsuario;
    this.borradorDias = this.totalesDe(c.idUsuario);
    // El campo aparece hasta el siguiente ciclo de detección de cambios. Se enfoca por
    // código y no con el atributo autofocus: el navegador solo lo atiende una vez por
    // página y Sonar lo marca por accesibilidad (Web:S9379).
    setTimeout(() => this.campoDias?.nativeElement.focus());
  }

  cancelaDias(): void {
    this.editandoDias = null;
    this.borradorDias = null;
  }

  /**
   * Guarda lo capturado en la celda.
   *
   * Se manda el saldo COMPLETO —área y autorizador incluidos— y no solo
   * los días: el backend guarda con INSERT ... ON DUPLICATE KEY UPDATE y
   * mandar el resto en nulo borraría lo que dejó el organigrama.
   *
   * Y con el `anio` DEL RENGLÓN que se ve (el año en que empieza su
   * periodo), nunca el del mes: la llave es (persona, anio), y a quien
   * cumple aniversario en octubre el periodo vigente le empezó el año
   * pasado. Con el del mes se habrían escrito los días en el siguiente.
   */
  guardaDias(c: Colaborador): void {
    if (c.idUsuario == null || this.editandoDias !== c.idUsuario) {
      return;
    }

    const antes = this.totalesDe(c.idUsuario);
    const nuevo = this.borradorDias;

    // Sin cambio no se molesta al servidor.
    if (nuevo === antes) {
      this.cancelaDias();
      return;
    }
    if (nuevo != null && (nuevo < 0 || nuevo > 60)) {
      this.aviso("Los días tienen que ir entre 0 y 60.", "warning");
      return;
    }

    const previo = this.saldoDe(c.idUsuario);
    const saldo: Saldo = {
      idUsuario: c.idUsuario,
      // Sin renglón no se manda: el servidor usa su periodo vigente.
      anio: previo && previo.anio != null ? previo.anio : undefined,
      diasTotales: nuevo == null ? undefined : nuevo,
      area: previo ? previo.area : undefined,
      idAutoriza: previo ? previo.idAutoriza : undefined,
      comentario: previo ? previo.comentario : "",
    };

    this.guardandoDias = c.idUsuario;
    this.editandoDias = null;

    const { desde, hasta } = this.rangoVisible();
    this.service.guardarSaldo(saldo, desde, hasta).subscribe({
      next: (res) => {
        this.guardandoDias = null;
        if (res.codigoError) {
          this.aviso(res.mensajeError || "No se pudo guardar.", "error");
          this.cdr.detectChanges();
          return;
        }
        // La respuesta ya es la lista que se ve, con los días nuevos y lo
        // que cambia con ellos (restantes, le quedan, prima).
        this.aplicaSaldos(res.saldos);
        this.cdr.detectChanges();
      },
      error: () => {
        this.guardandoDias = null;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /** El área del organigrama a la que pertenece. */
  /**
   * Un color por área.
   *
   * Se asigna por POSICIÓN en la lista ordenada de áreas, no por un
   * hash del nombre: con nueve áreas un hash repetiría tonos, y dos
   * áreas del mismo color se leen como si fueran la misma. La posición
   * garantiza que todas salgan distintas.
   *
   * Se calcula UNA vez al cargar y se guarda en el mapa; la plantilla
   * solo consulta, porque la llaman una vez por renglón en cada ciclo
   * de detección de cambios.
   */
  private readonly TONOS_AREA = [
    "#0369a1", // azul
    "#15803d", // verde
    "#b45309", // ámbar
    "#be123c", // frambuesa
    "#0f766e", // verde azulado
    "#7c2d12", // terracota
    "#1e40af", // azul marino
    "#a16207", // mostaza
    "#9d174d", // vino
    "#4d7c0f", // oliva
  ];

  private coloresArea = new Map<string, string>();

  /** Rearma el mapa de colores. Se llama al recibir los datos. */
  private asignaColoresArea(): void {
    const areas = new Set<string>();
    for (const c of this.colaboradores) {
      const a = this.areaDe(c.idUsuario);
      if (a) {
        areas.add(a);
      }
    }
    const orden = Array.from(areas).sort((a, b) => a.localeCompare(b, "es"));
    this.coloresArea = new Map<string, string>();
    orden.forEach((a, i) => {
      this.coloresArea.set(a, this.TONOS_AREA[i % this.TONOS_AREA.length]);
    });
  }

  /** El color de un área. Gris para quien no trae ninguna. */
  colorArea(area?: string): string {
    if (!area) {
      return "#94a3b8";
    }
    return this.coloresArea.get(area) || "#94a3b8";
  }

  areaDe(idUsuario?: number): string {
    const s = idUsuario != null ? this.saldoDe(idUsuario) : null;
    return s && s.area ? s.area : "";
  }

  /**
   * Los colaboradores que casan con el filtro, ordenados por área.
   *
   * El filtro busca en el nombre Y en el área, para poder escribir
   * "Desarrollo" y quedarse con esa gente. Ordenar por área junta a cada
   * equipo aunque la tabla vaya paginada; los que no traen área caen al
   * final, que son justo los que faltan por configurar.
   */
  get colaboradoresFiltrados(): Colaborador[] {
    const q = this.filtroConfig.trim().toLowerCase();

    // Primero el área elegida con la ficha, luego lo escrito. Los dos
    // filtros se suman: se puede estar en Desarrollo Y buscar un nombre.
    // Solo quien tiene periodo (ver enConfig): la gente del organigrama y
    // quien la carga del Excel dio de alta sin estar en él, que sale al
    // final como "Sin área".
    let lista = this.colaboradores.filter((c) => this.enConfig(c));
    if (this.areaFiltro === this.SIN_AREA) {
      lista = lista.filter((c) => !this.areaDe(c.idUsuario));
    } else if (this.areaFiltro) {
      lista = lista.filter((c) => this.areaDe(c.idUsuario) === this.areaFiltro);
    }
    if (q) {
      lista = lista.filter((c) => {
        const nombre = (c.nombre || "").toLowerCase();
        const area = this.areaDe(c.idUsuario).toLowerCase();
        return nombre.includes(q) || area.includes(q);
      });
    }

    return lista.sort((a, b) => {
      const aa = this.areaDe(a.idUsuario);
      const bb = this.areaDe(b.idUsuario);
      if (aa !== bb) {
        if (!aa) {
          return 1;
        }
        if (!bb) {
          return -1;
        }
        return aa.localeCompare(bb, "es");
      }
      return (a.nombre || "").localeCompare(b.nombre || "", "es");
    });
  }

  /**
   * Se queda con un área. Cadena vacía significa todas.
   *
   * ASIGNA, no alterna: antes venía de unas fichas donde volver a
   * pulsar la misma quitaba el filtro, pero ahora lo manda un select y
   * ahí alternar dejaría el valor mostrado y el filtro real diciendo
   * cosas distintas.
   */
  filtraPorArea(area: string): void {
    this.areaFiltro = area || "";
    this.paginaConfig = 1;
  }

  /**
   * Las áreas para las fichas del filtro, cada una con su gente.
   *
   * Se cuenta sobre TODOS los colaboradores y no sobre los filtrados:
   * si contara los filtrados, al elegir un área las demás fichas
   * marcarían cero y parecería que se vaciaron.
   */
  get fichasArea(): { area: string; cuantos: number }[] {
    const cuenta = new Map<string, number>();
    for (const c of this.colaboradores) {
      const a = this.areaDe(c.idUsuario) || "";
      cuenta.set(a, (cuenta.get(a) || 0) + 1);
    }

    const fichas: { area: string; cuantos: number }[] = [];
    for (const [area, cuantos] of cuenta) {
      if (area) {
        fichas.push({ area, cuantos });
      }
    }
    fichas.sort((x, y) => x.area.localeCompare(y.area, "es"));

    return fichas;
  }

  /** Cuántos hay de esa área en lo que se está viendo ahora. */
  cuentaEnArea(area: string): number {
    return this.colaboradoresFiltrados.filter(
      (c) => this.areaDe(c.idUsuario) === area
    ).length;
  }

  /** Cuánta gente sale en Configuración en total (con área o sin ella). */
  get totalOrganigrama(): number {
    return this.colaboradores.filter((c) => this.enConfig(c)).length;
  }

  /**
   * El valor del filtro para quien no tiene área. No es texto que pueda
   * traer un área de verdad del organigrama.
   */
  readonly SIN_AREA = "__sin_area__";

  /**
   * ¿Sale en Configuración? Quien tiene periodo: el área sale de ahí, así
   * que es la gente del organigrama y, desde el 24/09/2026, también quien
   * la carga del Excel de RH dio de alta sin estar en el organigrama (su
   * periodo nace sin área ni autorizador). Antes sólo salía quien traía
   * área, y ésos desaparecían justo cuando falta ponerlos en el
   * organigrama. Quien no tiene periodo sigue sin salir: no hay nada que
   * enseñarle.
   */
  private enConfig(c: Colaborador): boolean {
    return !!this.saldoDe(c.idUsuario);
  }

  /** Cuántos salen sin área: la opción "Sin área" del filtro, sólo si hay. */
  get sinArea(): number {
    return this.colaboradores.filter((c) => this.enConfig(c) && !this.areaDe(c.idUsuario))
      .length;
  }

  /** Cuántas áreas distintas hay en lo que se está viendo. */
  get areasVisibles(): number {
    const vistas = new Set<string>();
    for (const c of this.colaboradoresFiltrados) {
      const a = this.areaDe(c.idUsuario);
      if (a) {
        vistas.add(a);
      }
    }
    return vistas.size;
  }

  /** true si este renglón abre un área distinta al anterior de la
   *  página: sirve para marcar dónde empieza cada equipo. */
  abreArea(i: number): boolean {
    const pagina = this.colaboradoresPagina;
    if (i === 0) {
      return true;
    }
    return this.areaDe(pagina[i].idUsuario) !== this.areaDe(pagina[i - 1].idUsuario);
  }

  /**
   * Lo que de verdad puede pedir en su periodo («acumulados» en la hoja de
   * RH): días restantes + lo acumulado de periodos anteriores. null si no
   * le han asignado días: no es lo mismo "no le quedan" que "no sabemos
   * cuántos tiene". Puede ser negativo: quien tomó de más lo ve así, y su
   * siguiente periodo se lo descuenta.
   *
   * Lo manda el servidor ya calculado (disponibles) porque el acumulado
   * sale de la cadena de periodos anteriores, que aquí no se tiene. La
   * cuenta de abajo es sólo el respaldo por si llegara sin él. Lo
   * acumulado NO entra en la prima: esa sale de totalesDe().
   */
  quedanDeSaldo(s: Saldo | null | undefined): number | null {
    if (!s || s.diasTotales == null) {
      return null;
    }
    if (s.disponibles != null) {
      return s.disponibles;
    }
    return s.diasTotales + (s.diasArrastre || 0) - this.gozadosDe(s);
  }

  /** Lo que le queda a alguien en el periodo que está en pantalla. */
  quedanDe(idUsuario?: number): number | null {
    return this.quedanDeSaldo(this.saldoDe(idUsuario));
  }

  /** Quién le autoriza. Cadena vacía si no está configurado. */
  autorizaDe(idUsuario?: number): string {
    const s = idUsuario != null ? this.saldoDe(idUsuario) : null;
    return s && s.autorizaNombre ? s.autorizaNombre : "";
  }

  /** Días de goce del periodo en pantalla. La base de la prima. */
  totalesDe(idUsuario?: number): number | null {
    const s = idUsuario != null ? this.saldoDe(idUsuario) : null;
    return s && s.diasTotales != null ? s.diasTotales : null;
  }

  /**
   * GOZADOS = lo que COS tiene capturado con fechas dentro del periodo + lo
   * que la persona ya se tomó fuera de COS y trae la hoja de RH.
   *
   * Los dos sumandos son distintos a propósito: diasTomados lo calcula
   * el servidor de las vacaciones del calendario, y diasGozadosPrevios es
   * un bulto sin fechas cuadrado a fechaCorte. Sin sumarlos, a quien ya
   * descansó antes de que existiera COS se le regalarían esos días.
   */
  gozadosDe(s: Saldo | null | undefined): number {
    if (!s) {
      return 0;
    }
    return (s.diasTomados || 0) + (s.diasGozadosPrevios || 0);
  }

  /**
   * «Días restantes» de la hoja de RH: días de goce − gozados, SIN lo
   * acumulado. null sin días asignados. También lo manda el servidor.
   */
  restantesDe(s: Saldo | null | undefined): number | null {
    if (!s || s.diasTotales == null) {
      return null;
    }
    return s.restantes != null ? s.restantes : s.diasTotales - this.gozadosDe(s);
  }

  /**
   * Lo acumulado de periodos anteriores: lo que le sobró y no tomó. Lo
   * fija RH («Dias Pend» de la hoja) o sale solo de su periodo anterior
   * (arrastreCalculado). null si no se sabe.
   */
  acumuladoDe(s: Saldo | null | undefined): number | null {
    return s && s.diasArrastre != null ? s.diasArrastre : null;
  }

  /** "3 en COS · 5 antes de COS", para el tooltip de Gozados del panel de captura. */
  desgloseGozados(s: Saldo | null | undefined): string {
    return this.textoGozados(s?.diasTomados || 0, s?.diasGozadosPrevios || 0, s?.fechaCorte);
  }

  private textoGozados(enCos: number, previos: number, fechaCorte?: string | null): string {
    const hoja = fechaCorte && previos ? ` (según la hoja de RH al ${this.fechaCompleta(fechaCorte)})` : "";
    return `${enCos} en COS · ${previos} antes de COS${hoja}`;
  }

  /* ---------- La tabla: «lo que le sobra primero» (20/10/2026) ----------
   * La tabla de Configuración dice de cuál periodo salen los días
   * (../saldo-vista): lo que se pide para después del aniversario sale
   * primero de lo que le sobra al vigente. El panel de captura sigue con la
   * cuenta de siempre (gozadosDe, quedanDeSaldo), que es con la que valida
   * las fechas que se capturan. */

  /** «Gozados» de la tabla. */
  gozadosVistaDe(s: Saldo | null | undefined): number {
    return s ? gozadosVista(s) : 0;
  }

  /** «Restantes» de la tabla; null sin días asignados. */
  restantesVistaDe(s: Saldo | null | undefined): number | null {
    return s ? restantesVista(s) : null;
  }

  /** «Acumulado» de la tabla; null si no se sabe. */
  acumuladoVistaDe(s: Saldo | null | undefined): number | null {
    return s ? acumuladoVista(s) : null;
  }

  /** «Le quedan» de la tabla; null sin días asignados. */
  quedanVistaDe(s: Saldo | null | undefined): number | null {
    return s ? quedanVista(s) : null;
  }

  /** El tooltip de Gozados de la tabla, con lo que es de su siguiente periodo. */
  desgloseGozadosVista(s: Saldo | null | undefined): string {
    if (!s) {
      return "";
    }
    const enCos = gozadosEnCos(s);
    const base = this.textoGozados(enCos, s.diasGozadosPrevios || 0, s.fechaCorte);
    const delSiguiente = paraElSiguiente(s);
    if (!delSiguiente) {
      return base;
    }
    const cuantos = delSiguiente === enCos ? `Los ${delSiguiente}` : `De esos, ${delSiguiente}`;
    return `${base}. ${cuantos} son de lo que pidió para su siguiente periodo: se toman primero de lo que le sobra aquí.`;
  }

  /** El tooltip de Le quedan de la tabla. */
  desgloseQuedanVista(s: Saldo | null | undefined): string {
    return s ? this.textoQuedan(restantesVista(s), acumuladoVista(s) || 0) : "";
  }

  /** "12 restantes + 3 acumulados", para el tooltip de Le quedan del panel de captura. */
  desgloseQuedan(s: Saldo | null | undefined): string {
    return this.textoQuedan(this.restantesDe(s), this.acumuladoDe(s) || 0);
  }

  private textoQuedan(restantes: number | null, acumulado: number): string {
    if (restantes === null) {
      return "";
    }
    const base = `${restantes} ${restantes === 1 ? "restante" : "restantes"} de su periodo`;
    if (!acumulado) {
      return base;
    }
    return acumulado > 0
      ? `${base} + ${acumulado} ${acumulado === 1 ? "acumulado" : "acumulados"} de periodos anteriores`
      : `${base} − ${-acumulado} que tomó de más en periodos anteriores`;
  }

  /** De dónde sale lo acumulado de la tabla, y cuánto ya se usó, para su tooltip. */
  tipAcumulado(s: Saldo | null | undefined): string {
    const n = this.acumuladoVistaDe(s);
    if (n === null || !s) {
      return "";
    }
    const usado = acumuladoUsado(s);
    const yaUsado = usado ? ` Ya se usaron ${usado}: lo que le sobra se usa primero.` : "";
    if (s.arrastreCalculado) {
      if (n === 0) {
        return usado
          ? `Lo que le sobró de su periodo anterior (${usado}) ya se usó: lo que le sobra se usa primero.`
          : "No trae días acumulados de periodos anteriores.";
      }
      return n > 0
        ? `Le sobraron de su periodo anterior; se calcula solo.${yaUsado} Suman a lo que le queda, no a la prima.`
        : "Tomó de más en su periodo anterior; se le descuenta de éste.";
    }
    return `Lo fijó RH («Dias Pend» de la hoja) o se fijó al capturar su fecha de ingreso.${yaUsado} ` +
      "Suma a lo que le queda, no a la prima.";
  }

  /** Años cumplidos al empezar el periodo y los días de ley que dan. */
  tipAntiguedad(s: Saldo | null | undefined): string {
    if (!s || s.antiguedad == null) {
      return "";
    }
    const a = s.antiguedad;
    const inicio = s.periodoInicio
      ? ` al inicio de su periodo (${this.fechaCompleta(s.periodoInicio)})`
      : "";
    const ley = s.diasPorLey != null
      ? `: por ley le tocan ${s.diasPorLey} ${s.diasPorLey === 1 ? "día" : "días"} (LFT art. 76)`
      : "";
    return `${a} ${a === 1 ? "año cumplido" : "años cumplidos"}${inicio}${ley}`;
  }

  /** La Alta IMSS (fecha de ingreso): la del periodo o, si no trae, la de la persona. */
  altaDe(idUsuario?: number): string {
    const s = this.saldoDe(idUsuario);
    return (s && s.fechaIngreso) || this.fechaIngresoDe(idUsuario) || "";
  }

  /* ---------- El periodo ----------
   *
   * Desde el 24/09/2026 es la UNIDAD del saldo: cada renglón es un periodo
   * del aniversario de su Alta IMSS al siguiente, como la hoja de RH (o el
   * año calendario de quien no la tiene), y lo que no se toma en uno pasa
   * al siguiente como acumulado. Las fechas las pone el servidor; aquí
   * sólo se enseñan. */

  /** "2025-10-01" → "01/10/2025", como en la hoja de RH. Se parte el texto
   *  y no se hace un Date: un día no tiene hora y así no hay zona horaria
   *  que lo corra. */
  fechaCompleta(iso?: string | null): string {
    if (!iso || iso.length < 10) {
      return "";
    }
    return `${iso.substring(8, 10)}/${iso.substring(5, 7)}/${iso.substring(0, 4)}`;
  }

  /**
   * "del 20/09/2026 al 20/09/2027", en la forma de la hoja de RH: su «Fin
   * del periodo» es el aniversario siguiente, y ese día ya es del periodo
   * que sigue. "" si no hay periodo.
   */
  periodoTexto(s: Saldo | null | undefined): string {
    if (!s || !s.periodoInicio || !s.periodoFin) {
      return "";
    }
    return `del ${this.fechaCompleta(s.periodoInicio)} al ${this.fechaCompleta(s.periodoFin)}`;
  }

  /** El periodo en pantalla de alguien, sólo si trae sus fechas. */
  periodoDe(idUsuario?: number): Saldo | null {
    const s = this.saldoDe(idUsuario);
    return s && s.periodoInicio && s.periodoFin ? s : null;
  }

  /** Días de hoy al fin del periodo (el aniversario siguiente). 0 o menos = ya terminó. */
  private diasParaCerrar(iso?: string | null): number | null {
    if (!iso || iso.length < 10) {
      return null;
    }
    const fin = this.aFecha(iso.substring(0, 10)).getTime();
    const hoy = this.aFecha(this.hoyIso).getTime();
    return Math.round((fin - hoy) / 86400000);
  }

  /**
   * La pastilla de la columna Periodo.
   *
   * Los días de un periodo ya no "vencen": lo que sobra pasa al siguiente
   * como acumulado. Por eso dice «Cerrado» (se está viendo un mes pasado y
   * ese periodo ya terminó) y «Por cerrar» (faltan dos meses o menos para
   * su aniversario, el margen con el que RH alcanza a acomodar el descanso).
   */
  estadoPeriodo(idUsuario?: number): "" | "CERRADO" | "PORCERRAR" {
    const s = this.saldoDe(idUsuario);
    const faltan = s ? this.diasParaCerrar(s.periodoFin) : null;
    if (!s || faltan === null) {
      return "";
    }
    // El fin es exclusivo: el día del aniversario ya es del siguiente.
    if (faltan <= 0) {
      return "CERRADO";
    }
    // Uno que ni ha empezado (se ve un mes futuro) no tiene nada que avisar.
    if (s.periodoInicio && s.periodoInicio.substring(0, 10) > this.hoyIso) {
      return "";
    }
    return faltan <= 60 ? "PORCERRAR" : "";
  }

  /** El tooltip de la columna Periodo, con el porqué de la pastilla. */
  tipPeriodo(idUsuario?: number): string {
    const s = this.periodoDe(idUsuario);
    if (!s) {
      return "";
    }
    const base = s.porAniversario === false
      ? `Año calendario, ${this.periodoTexto(s)}: le falta la Alta IMSS. Captúrala con el lápiz ` +
        "para que sus periodos vayan de aniversario a aniversario."
      : `Periodo de aniversario de su Alta IMSS, ${this.periodoTexto(s)}.`;
    const faltan = this.diasParaCerrar(s.periodoFin);
    if (faltan === null) {
      return base;
    }
    if (faltan <= 0) {
      return `${base} Ya terminó: lo que le quedó pasó a su siguiente periodo como acumulado.`;
    }
    if ((s.periodoInicio || "").substring(0, 10) > this.hoyIso) {
      return `${base} Todavía no empieza.`;
    }
    return `${base} Su siguiente periodo empieza el ${this.fechaCompleta(s.periodoFin)} ` +
      `(en ${faltan} ${faltan === 1 ? "día" : "días"}): lo que no tome pasa ahí como acumulado.`;
  }

  trackColaborador(_: number, c: Colaborador): number {
    return c.idUsuario || 0;
  }

  /* ---------- Paginado de configuración ---------- */

  get totalPaginasConfig(): number {
    return Math.max(1, Math.ceil(this.colaboradoresFiltrados.length / this.porPaginaConfig));
  }

  /**
   * La página que de verdad se puede mostrar.
   *
   * Se ACOTA aquí en vez de corregir this.paginaConfig: escribir en una
   * propiedad desde un getter que llama la plantilla deja la detección
   * de cambios dando vueltas y cuelga la pantalla entera. El reajuste
   * de verdad se hace en cambiaFiltro().
   */
  private get paginaValida(): number {
    return Math.min(Math.max(1, this.paginaConfig), this.totalPaginasConfig);
  }

  /** Solo los de la página actual. */
  get colaboradoresPagina(): Colaborador[] {
    const desde = (this.paginaValida - 1) * this.porPaginaConfig;
    return this.colaboradoresFiltrados.slice(desde, desde + this.porPaginaConfig);
  }

  /** Al escribir en el buscador se vuelve a la primera página: si no, se
   *  queda en una que el filtro ya dejó vacía. */
  cambiaFiltro(valor: string): void {
    this.filtroConfig = valor;
    this.paginaConfig = 1;
  }

  irPaginaConfig(n: number): void {
    if (n >= 1 && n <= this.totalPaginasConfig) {
      this.paginaConfig = n;
    }
  }

  cambiaTamano(n: number): void {
    this.porPaginaConfig = n;
    this.paginaConfig = 1;
  }

  /**
   * Los botones de página, en ventana.
   *
   * Con 139 colaboradores de 10 en 10 son 14 páginas: pintarlas todas
   * llena el renglón y no ayuda a nadie. Se enseñan la primera, la
   * última y dos a cada lado de la actual; el 0 es el hueco (…).
   */
  get paginasConfig(): number[] {
    const total = this.totalPaginasConfig;
    const act = this.paginaConfig;

    if (total <= 7) {
      const todas: number[] = [];
      for (let i = 1; i <= total; i++) {
        todas.push(i);
      }
      return todas;
    }

    const cerca = new Set<number>([1, total, act]);
    for (let d = 1; d <= 2; d++) {
      if (act - d >= 1) {
        cerca.add(act - d);
      }
      if (act + d <= total) {
        cerca.add(act + d);
      }
    }

    const orden = Array.from(cerca).sort((a, b) => a - b);
    const conHuecos: number[] = [];
    let previa = 0;
    for (const n of orden) {
      if (previa && n - previa > 1) {
        conHuecos.push(0);
      }
      conHuecos.push(n);
      previa = n;
    }
    return conHuecos;
  }

  /** Primer y último renglón que se está viendo, para el "x-y de z". */
  get desdeConfig(): number {
    return this.colaboradoresFiltrados.length === 0
      ? 0
      : (this.paginaValida - 1) * this.porPaginaConfig + 1;
  }

  get hastaConfig(): number {
    return Math.min(this.paginaValida * this.porPaginaConfig,
                    this.colaboradoresFiltrados.length);
  }

  /** Marca el tirón que da el panel cuando se hace clic afuera. */
  tiron = false;

  /**
   * Clic en el fondo. Antes cerraba el panel, y bastaba un clic de mas
   * para perder todo lo capturado sin aviso.
   *
   * Ahora no cierra. Pero dejarlo sin ninguna reaccion se siente como si
   * la pantalla se hubiera colgado, asi que el panel da un tirón corto
   * que apunta a los botones del pie.
   */
  insisteEnCerrar(): void {
    if (this.tiron) {
      return; // ya esta sacudiendose; no encimar animaciones
    }

    this.tiron = true;
    setTimeout(() => {
      this.tiron = false;
      this.cdr.detectChanges();
    }, 380);
  }

  /** El color elegido no es ninguno de los ocho de la paleta, o sea que
   *  el usuario lo creo el mismo. Se compara en minusculas porque el
   *  input nativo siempre devuelve el hex asi y en la base pudo quedar
   *  guardado de otra forma. */
  get colorEsLibre(): boolean {
    const c = (this.borrador.color || "").toLowerCase();
    return !!c && this.COLORES.indexOf(c) === -1;
  }

  /** Color salido del selector del sistema. Se normaliza a minusculas
   *  para que 'is-elegido' compare bien contra la paleta. */
  eligeColorLibre(valor: string): void {
    this.borrador.color = (valor || "").toLowerCase();
  }

  private aFecha(iso: string): Date {
    const p = iso.split("-");
    return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
  }

  private iso(d: Date): string {
    const mes = `${d.getMonth() + 1}`.padStart(2, "0");
    const dia = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${mes}-${dia}`;
  }

  private aviso(texto: string, icono: "success" | "error" | "warning"): void {
    swal.fire({
      text: texto,
      icon: icono,
      confirmButtonText: "Entendido",
      confirmButtonColor: "#b71c1c",
    });
  }

  /** Las dos ultimas columnas son sabado y domingo. */
  esColumnaFinde(i: number): boolean {
    return i >= 5;
  }

  /**
   * El color del colaborador con transparencia, para el acabado de
   * cristal: el fondo es el mismo tono muy diluido y el texto va en el
   * color pleno, que se lee mejor que blanco sobre pastel.
   */
  tinte(color: string | undefined, alpha: number): string {
    if (!color || color.length < 7) {
      return `rgba(100, 116, 139, ${alpha})`;
    }
    const r = Number.parseInt(color.substring(1, 3), 16);
    const g = Number.parseInt(color.substring(3, 5), 16);
    const b = Number.parseInt(color.substring(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /** Para que *ngFor no repinte todo el calendario en cada carga. */
  trackDia(_: number, d: DiaCalendario): string {
    return d.iso || `x${_}`;
  }

  trackVacacion(_: number, v: Vacacion): number {
    return v.idVacacion || _;
  }
}
